import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import "./App.css";
import axios from "axios";
import { useLanguage } from "./i18n";
import LanguageSelector from "./components/LanguageSelector";
import ProDashboard from "./components/pro/ProDashboard";
import StrategyWizard from "./components/strategy/StrategyWizard";
import StrategyPreview from "./components/strategy/StrategyPreview";
import NewLandingPage from "./components/strategy/NewLandingPage";
import FullStrategyView from "./components/strategy/FullStrategyView";
import ProjectsDashboard from "./components/strategy/ProjectsDashboard";
import NotreApproche from "./components/NotreApproche";
import { 
  getAuthToken, 
  setAuthToken as saveAuthToken, 
  removeAuthToken,
  getPendingProjectId,
  setPendingProjectId,
  removePendingProjectId 
} from "./utils/auth";

// Configure axios timeout for long-running OpenAI requests
axios.defaults.timeout = 60000; // 60 seconds

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Optimized form input that manages its own local state
const FormInput = memo(({ value, onChange, type = 'text', placeholder, className, required, minLength, min, max, 'data-testid': testId }) => {
  const [localValue, setLocalValue] = useState(value);
  
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  const handleChange = (e) => {
    const newValue = type === 'number' ? e.target.value : e.target.value;
    setLocalValue(newValue);
  };
  
  const handleBlur = () => {
    if (localValue !== value) {
      onChange(type === 'number' ? parseInt(localValue) || 0 : localValue);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleBlur();
    }
  };
  
  return (
    <input
      type={type}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className={className}
      required={required}
      minLength={minLength}
      min={min}
      max={max}
      data-testid={testId}
    />
  );
});

FormInput.displayName = 'FormInput';

// Optimized textarea
const FormTextarea = memo(({ value, onChange, placeholder, className, rows, 'data-testid': testId }) => {
  const [localValue, setLocalValue] = useState(value);
  
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  const handleChange = (e) => {
    setLocalValue(e.target.value);
  };
  
  const handleBlur = () => {
    if (localValue !== value) {
      onChange(localValue);
    }
  };
  
  return (
    <textarea
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
      rows={rows}
      data-testid={testId}
    />
  );
});

FormTextarea.displayName = 'FormTextarea';

function App() {
  const { t } = useLanguage();
  const [currentPage, setCurrentPage] = useState('home');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [userContent, setUserContent] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loyaltyStatus, setLoyaltyStatus] = useState(null);
  const [authToken, setAuthToken] = useState(getAuthToken());
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [contentForm, setContentForm] = useState({
    content_type: 'blog',
    topic: '',
    tone: 'professional',
    word_count: 500,
    industry: '',
    additional_instructions: ''
  });
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [showTemplatesSection, setShowTemplatesSection] = useState(false);
  
  // Eco/RSE state
  const [ecoImpact, setEcoImpact] = useState(null);
  const [ecoLeaderboard, setEcoLeaderboard] = useState(null);
  const [rseTemplates, setRseTemplates] = useState(null);
  const [ecoAchievements, setEcoAchievements] = useState(null);
  
  // Actualites/News state
  const [newsData, setNewsData] = useState(null);
  const [currentTip, setCurrentTip] = useState(null);
  const [newsLoading, setNewsLoading] = useState(false);
  
  // Password reset state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  
  // Admin panel state
  const [adminUsers, setAdminUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [adminView, setAdminView] = useState('stats'); // 'stats' | 'users' | 'analytics'
  const [advancedAnalytics, setAdvancedAnalytics] = useState(null);

  // Email verification state
  const [showVerificationBanner, setShowVerificationBanner] = useState(false);
  const [verificationSending, setVerificationSending] = useState(false);
  
  // Two-Factor Authentication state
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFactorSecret, setTwoFactorSecret] = useState('');
  const [twoFactorQRUri, setTwoFactorQRUri] = useState('');
  const [twoFactorQRImage, setTwoFactorQRImage] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [pending2FAUserId, setPending2FAUserId] = useState(null);
  const [pending2FAEmail, setPending2FAEmail] = useState('');
  const [pending2FAPassword, setPending2FAPassword] = useState('');

  // Strategy Wizard state (New VF Actions 2.0)
  const [strategyPreview, setStrategyPreview] = useState(null);
  const [wizardInitialType, setWizardInitialType] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);

  // Check for reset_token or session_id or verify_email in URL on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetTokenParam = urlParams.get('reset_token');
    const verifyEmailToken = urlParams.get('verify_email');
    
    if (resetTokenParam) {
      setResetToken(resetTokenParam);
      setShowResetPasswordModal(true);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (verifyEmailToken) {
      verifyEmail(verifyEmailToken);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Check for Google OAuth session_id in URL hash
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        processGoogleSession(sessionId);
      }
    }
  }, []);

  // Initialize app and check authentication
  useEffect(() => {
    if (authToken) {
      initializeAuthenticatedUser();
    }
    loadIndustries();
    checkAdminAccess();
  }, []);

  // Configure axios with auth token
  useEffect(() => {
    if (authToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [authToken]);

  // Load loyalty status when user is set
  useEffect(() => {
    if (user && authToken) {
      loadLoyaltyStatus();
      loadTemplates();
    }
  }, [user, authToken]);

  const initializeAuthenticatedUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      setUser(userData);
      setIsAdmin(userData.is_admin || false);
      
      if (userData.is_admin) {
        loadAdminStats();
      }
    } catch (error) {
      console.error("Error getting user info:", error);
      removeAuthToken();
      setAuthToken(null);
      setUser(null);
      setIsAdmin(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
      const payload = authMode === 'login' 
        ? { email: authForm.email, password: authForm.password }
        : authForm;
        
      const response = await axios.post(`${API}${endpoint}`, payload);
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setPending2FAUserId(response.data.user_id);
        setPending2FAEmail(authForm.email);
        setPending2FAPassword(authForm.password);
        setLoading(false);
        return;
      }
      
      const { access_token, user: userData } = response.data;
      
      saveAuthToken(access_token);
      setAuthToken(access_token);
      setUser(userData);
      setIsAdmin(userData.is_admin || false);
      
      // Check if email needs verification
      if (!userData.email_verified) {
        setShowVerificationBanner(true);
      }
      
      setShowAuthModal(false);
      setAuthForm({ email: '', password: '', name: '' });
      
      // Check for pending demo project to convert
      const pendingProjectId = getPendingProjectId();
      if (pendingProjectId && authMode === 'register') {
        // Convert demo project to user's project and generate full content
        // user_id is now derived from the JWT token on the backend (secured)
        try {
          const convertResponse = await axios.post(
            `${API}/projects/convert-demo/${pendingProjectId}`,
            null,
            { 
              headers: { 'Authorization': `Bearer ${access_token}` }
            }
          );
          
          if (convertResponse.data.success) {
            removePendingProjectId();
            setCurrentProjectId(pendingProjectId);
            setCurrentPage('strategy');
            alert('Compte créé ! Votre stratégie marketing complète est en cours de génération...');
            return;
          }
        } catch (convertError) {
          console.error('Error converting demo project:', convertError);
          // Continue with normal flow if conversion fails
        }
      }
      
      alert(authMode === 'login' ? 'Connexion reussie !' : 'Compte cree ! Verifiez votre email.');
      
      if (userData.is_admin) {
        loadAdminStats();
      }
      
    } catch (error) {
      alert(error.response?.data?.detail || `Erreur ${authMode === 'login' ? 'de connexion' : "d'inscription"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    removeAuthToken();
    setAuthToken(null);
    setUser(null);
    setIsAdmin(false);
    setLoyaltyStatus(null);
    setGeneratedContent('');
    setUserContent([]);
    setCurrentPage('home');
    // Also call backend logout to clear session cookie
    axios.post(`${API}/auth/logout`).catch(() => {});
    alert('Deconnexion reussie !');
  };

  // ==================== GOOGLE OAUTH FUNCTIONS ====================
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/google/session`, {
        session_id: sessionId
      });
      
      const { user: userData, session_token } = response.data;
      
      saveAuthToken(session_token);
      setAuthToken(session_token);
      setUser(userData);
      setIsAdmin(userData.is_admin || false);
      
      // Clear URL hash
      window.history.replaceState({}, document.title, window.location.pathname);
      
      setShowAuthModal(false);
      
      // Check for pending demo project to convert (same as email registration)
      const pendingProjectId = getPendingProjectId();
      if (pendingProjectId) {
        try {
          const convertResponse = await axios.post(
            `${API}/projects/convert-demo/${pendingProjectId}`,
            null,
            { headers: { 'Authorization': `Bearer ${session_token}` } }
          );
          
          if (convertResponse.data.success) {
            removePendingProjectId();
            setCurrentProjectId(pendingProjectId);
            setCurrentPage('strategy');
            alert('Connexion Google réussie ! Votre stratégie marketing est en cours de génération...');
            
            if (userData.is_admin) {
              loadAdminStats();
            }
            return;
          }
        } catch (convertError) {
          console.error('Error converting demo project after Google login:', convertError);
          // Continue with normal flow if conversion fails
        }
      }
      
      alert('Connexion Google réussie !');
      
      if (userData.is_admin) {
        loadAdminStats();
      }
    } catch (error) {
      console.error("Google login error:", error);
      alert(error.response?.data?.detail || "Erreur de connexion Google");
      window.history.replaceState({}, document.title, window.location.pathname);
    } finally {
      setLoading(false);
    }
  };

  // ==================== ADMIN USER MANAGEMENT FUNCTIONS ====================
  
  const loadAdminUsers = async () => {
    if (!authToken || !isAdmin) return;
    try {
      const response = await axios.get(`${API}/admin/users`);
      setAdminUsers(response.data.users || []);
    } catch (error) {
      console.error("Error loading admin users:", error);
    }
  };

  const loadAdvancedAnalytics = async () => {
    if (!authToken || !isAdmin) return;
    try {
      const response = await axios.get(`${API}/admin/analytics`);
      setAdvancedAnalytics(response.data);
    } catch (error) {
      console.error("Error loading analytics:", error);
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`);
      setSelectedUser(response.data);
      setShowUserModal(true);
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors du chargement");
    }
  };

  const updateUserPlan = async (userId, newPlan) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, {
        subscription_plan: newPlan
      });
      alert("Plan mis a jour !");
      loadAdminUsers();
      if (selectedUser) {
        viewUserDetails(userId);
      }
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de la mise a jour");
    }
  };

  const adjustUserCredits = async (userId, amount, reason = "") => {
    try {
      await axios.post(`${API}/admin/users/${userId}/credits`, {
        amount: amount,
        reason: reason
      });
      alert(`Credits ${amount > 0 ? 'ajoutes' : 'retires'} !`);
      loadAdminUsers();
      if (selectedUser) {
        viewUserDetails(userId);
      }
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de l'ajustement");
    }
  };

  const toggleUserAdmin = async (userId, makeAdmin) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, {
        is_admin: makeAdmin
      });
      alert(makeAdmin ? "Droits admin accordes !" : "Droits admin retires !");
      loadAdminUsers();
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur");
    }
  };

  const deactivateUser = async (userId) => {
    if (!window.confirm("Desactiver ce compte utilisateur ?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      alert("Compte desactive !");
      loadAdminUsers();
      setShowUserModal(false);
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur");
    }
  };

  const generateResetLinkForUser = async (userId) => {
    try {
      const response = await axios.post(`${API}/admin/users/${userId}/reset-password`);
      const link = response.data.reset_link;
      await navigator.clipboard.writeText(link);
      alert("Lien de reinitialisation copie dans le presse-papier !");
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur");
    }
  };

  // ==================== PASSWORD RESET FUNCTIONS ====================
  
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail.trim()) {
      alert("Veuillez entrer votre email");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotPasswordEmail
      });
      
      alert(response.data.message);
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de l'envoi de l'email");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      alert("Le mot de passe doit contenir au moins 6 caracteres");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      
      alert(response.data.message);
      setShowResetPasswordModal(false);
      setResetToken('');
      setNewPassword('');
      setShowAuthModal(true);
      setAuthMode('login');
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de la reinitialisation");
    } finally {
      setLoading(false);
    }
  };

  // ==================== EMAIL VERIFICATION FUNCTIONS ====================
  
  const verifyEmail = async (token) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { token });
      alert(response.data.message || "Email verifie avec succes !");
      // Refresh user data
      if (authToken) {
        initializeAuthenticatedUser();
      }
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de la verification");
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationEmail = async () => {
    setVerificationSending(true);
    try {
      const response = await axios.post(`${API}/auth/resend-verification`);
      alert(response.data.message || "Email de verification envoye !");
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setVerificationSending(false);
    }
  };

  // ==================== TWO-FACTOR AUTHENTICATION FUNCTIONS ====================
  
  const setup2FA = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/setup`);
      setTwoFactorSecret(response.data.secret);
      setTwoFactorQRUri(response.data.qr_uri);
      setTwoFactorQRImage(response.data.qr_code_image || '');
      setShow2FASetup(true);
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de la configuration 2FA");
    } finally {
      setLoading(false);
    }
  };

  const verify2FASetup = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      alert("Entrez un code a 6 chiffres");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/verify-setup`, { code: twoFACode });
      setBackupCodes(response.data.backup_codes);
      setShowBackupCodes(true);
      setShow2FASetup(false);
      setTwoFACode('');
      // Refresh user data
      initializeAuthenticatedUser();
      alert("2FA active avec succes !");
    } catch (error) {
      alert(error.response?.data?.detail || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  const disable2FA = async () => {
    const code = prompt("Entrez votre code 2FA pour desactiver:");
    if (!code) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { code });
      // Refresh user data
      initializeAuthenticatedUser();
      alert("2FA desactive avec succes");
    } catch (error) {
      alert(error.response?.data?.detail || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  const loadBackupCodes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/auth/2fa/backup-codes`);
      setBackupCodes(response.data.backup_codes);
      setShowBackupCodes(true);
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const regenerateBackupCodes = async () => {
    const code = prompt("Entrez votre code 2FA pour regenerer les codes:");
    if (!code) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/regenerate-backup-codes`, { code });
      setBackupCodes(response.data.backup_codes);
      alert("Nouveaux codes generes !");
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const loginWith2FA = async () => {
    if (!twoFACode) {
      alert("Entrez le code 2FA");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login-2fa`, {
        email: pending2FAEmail,
        password: pending2FAPassword,
        code: twoFACode
      });
      
      const { access_token, user } = response.data;
      setAuthToken(access_token);
      saveAuthToken(access_token);
      setUser(user);
      setIsAdmin(user.is_admin || false);
      setRequires2FA(false);
      setPending2FAUserId(null);
      setPending2FAEmail('');
      setPending2FAPassword('');
      setTwoFACode('');
      setShowAuthModal(false);
      
      if (user.is_admin) {
        loadAdminStats();
      }
    } catch (error) {
      alert(error.response?.data?.detail || "Code 2FA invalide");
    } finally {
      setLoading(false);
    }
  };

  const loadAdminStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setAdminStats(response.data);
    } catch (error) {
      console.error("Error loading admin stats:", error);
      setAdminStats({
        totalUsers: 0,
        activeSubscriptions: 0,
        monthlyRevenue: 0,
        contentGenerated: 0,
        topPlan: 'Starter',
        conversionRate: 0,
        yearlyProjection: 0,
        averageRevenuePerUser: 0
      });
    }
  };

  // ==================== ECO/RSE FUNCTIONS ====================
  
  const loadEcoImpact = async () => {
    if (!authToken) return;
    try {
      const response = await axios.get(`${API}/eco/impact`);
      setEcoImpact(response.data);
    } catch (error) {
      console.error("Error loading eco impact:", error);
    }
  };

  const loadEcoLeaderboard = async () => {
    if (!authToken) return;
    try {
      const response = await axios.get(`${API}/eco/leaderboard`);
      setEcoLeaderboard(response.data);
    } catch (error) {
      console.error("Error loading leaderboard:", error);
    }
  };

  const loadRseTemplates = async () => {
    try {
      const response = await axios.get(`${API}/eco/rse-templates`);
      setRseTemplates(response.data.templates);
    } catch (error) {
      console.error("Error loading RSE templates:", error);
    }
  };

  const loadEcoAchievements = async () => {
    if (!authToken) return;
    try {
      const response = await axios.get(`${API}/eco/achievements`);
      setEcoAchievements(response.data);
    } catch (error) {
      console.error("Error loading achievements:", error);
    }
  };

  // ==================== ACTUALITES / NEWS FUNCTIONS ====================
  
  const NEWS_CACHE_KEY = 'vf_news_cache';
  const NEWS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  
  const loadNewsData = async () => {
    // 1. Try to load from cache first for instant display
    const cached = localStorage.getItem(NEWS_CACHE_KEY);
    if (cached) {
      try {
        const { data, timestamp } = JSON.parse(cached);
        const isExpired = Date.now() - timestamp > NEWS_CACHE_DURATION;
        
        // Show cached data immediately
        if (data) {
          setNewsData(data);
          setNewsLoading(false);
        }
        
        // If cache is still fresh, don't refetch
        if (!isExpired) {
          return;
        }
      } catch (e) {
        console.error("Cache parse error:", e);
      }
    }
    
    // 2. Fetch fresh data (in background if cache was shown)
    if (!newsData) {
      setNewsLoading(true);
    }
    
    try {
      const response = await axios.get(`${API}/news/all`);
      setNewsData(response.data);
      
      // 3. Update cache
      localStorage.setItem(NEWS_CACHE_KEY, JSON.stringify({
        data: response.data,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error("Error loading news:", error);
    } finally {
      setNewsLoading(false);
    }
  };

  const clearNewsCache = () => {
    localStorage.removeItem(NEWS_CACHE_KEY);
  };

  const loadRandomTip = async () => {
    try {
      const response = await axios.get(`${API}/tips/random`);
      setCurrentTip(response.data.tip);
    } catch (error) {
      console.error("Error loading tip:", error);
    }
  };

  const offsetCarbon = async (amountKg = 0) => {
    try {
      const response = await axios.post(`${API}/eco/offset`, null, {
        params: { amount_kg: amountKg }
      });
      alert(response.data.message);
      loadEcoImpact();
      
      if (response.data.achievements_earned?.length > 0) {
        const achievementNames = response.data.achievements_earned.map(a => a.name).join(", ");
        alert(`Nouveaux badges debloques: ${achievementNames}`);
      }
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de la compensation");
    }
  };

  const checkAdminAccess = () => {
    const adminKey = new URLSearchParams(window.location.search).get('admin');
    if (adminKey === 'vfactions2025') {
      // Admin access via URL, will be verified when user loads
    }
  };

  const generateContent = async () => {
    if (!contentForm.topic.trim()) {
      alert("Veuillez entrer un sujet");
      return;
    }

    if (!authToken) {
      setShowAuthModal(true);
      setAuthMode('login');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate-content`, contentForm, {
        timeout: 60000
      });
      setGeneratedContent(response.data.content);
      setUser(prevUser => ({
        ...prevUser,
        credits_used: response.data.credits_used
      }));
      
      if (response.data.loyalty_points_earned) {
        alert(`Contenu genere ! +${response.data.loyalty_points_earned} points de fidelite !`);
        loadLoyaltyStatus();
      }
      
    } catch (error) {
      console.error("Content generation error:", error);
      if (error.response?.status === 401) {
        setShowAuthModal(true);
        setAuthMode('login');
        alert("Veuillez vous connecter pour generer du contenu");
      } else {
        alert(error.response?.data?.detail || "Echec de la generation de contenu");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadLoyaltyStatus = async () => {
    if (!authToken) return;
    try {
      const response = await axios.get(`${API}/loyalty/status`);
      setLoyaltyStatus(response.data);
    } catch (error) {
      console.error("Error loading loyalty status:", error);
    }
  };

  const loadUserContent = async () => {
    if (!authToken) return;
    try {
      const response = await axios.get(`${API}/my-content`);
      setUserContent(response.data);
    } catch (error) {
      console.error("Error loading user content:", error);
    }
  };

  const redeemReward = async (rewardType, cost) => {
    if (!authToken) return;
    try {
      const response = await axios.post(`${API}/loyalty/redeem`, null, {
        params: { reward_type: rewardType, cost: cost }
      });
      alert(`${response.data.message}`);
      loadLoyaltyStatus();
    } catch (error) {
      alert(error.response?.data?.detail || "Echec de l'echange");
    }
  };

  const loadIndustries = async () => {
    try {
      const response = await axios.get(`${API}/industries`);
      setIndustries(response.data.industries);
    } catch (error) {
      console.error("Error loading industries:", error);
    }
  };

  // ==================== TEMPLATE FUNCTIONS ====================
  
  const loadTemplates = async () => {
    if (!authToken) return;
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error loading templates:", error);
    }
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      alert("Veuillez entrer un nom pour le template");
      return;
    }
    
    try {
      const templateData = {
        name: templateName,
        content_type: contentForm.content_type,
        topic: contentForm.topic,
        tone: contentForm.tone,
        word_count: contentForm.word_count,
        industry: contentForm.industry || null,
        additional_instructions: contentForm.additional_instructions || null
      };
      
      const response = await axios.post(`${API}/templates`, templateData);
      alert("Template sauvegarde avec succes !");
      setShowSaveTemplateModal(false);
      setTemplateName('');
      loadTemplates();
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de la sauvegarde du template");
    }
  };

  const loadTemplate = async (templateId) => {
    try {
      const response = await axios.post(`${API}/templates/${templateId}/use`);
      const formData = response.data.form_data;
      setContentForm({
        content_type: formData.content_type || 'blog',
        topic: formData.topic || '',
        tone: formData.tone || 'professional',
        word_count: formData.word_count || 500,
        industry: formData.industry || '',
        additional_instructions: formData.additional_instructions || ''
      });
      alert("Template charge !");
      setShowTemplatesSection(false);
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors du chargement du template");
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm("Supprimer ce template ?")) return;
    
    try {
      await axios.delete(`${API}/templates/${templateId}`);
      alert("Template supprime");
      loadTemplates();
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const toggleFavorite = async (templateId) => {
    try {
      const response = await axios.post(`${API}/templates/${templateId}/favorite`);
      loadTemplates();
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  const initiatePayment = async (planId) => {
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout/session`, {
        plan_id: planId,
        origin_url: originUrl
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Payment initiation failed');
    }
  };

  // Auth Modal Component
  const AuthModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="auth-modal">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">
            {requires2FA ? 'Verification 2FA' : (showForgotPassword ? 'Mot de passe oublie' : (authMode === 'login' ? 'Connexion' : 'Inscription'))}
          </h2>
          <button 
            onClick={() => {
              setShowAuthModal(false);
              setShowForgotPassword(false);
              setRequires2FA(false);
              setPending2FAUserId(null);
              setPending2FAEmail('');
              setPending2FAPassword('');
              setTwoFACode('');
            }}
            className="text-gray-500 hover:text-gray-700 text-xl"
            data-testid="close-auth-modal"
          >
            X
          </button>
        </div>

        {requires2FA ? (
          // 2FA Verification Form
          <div>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                </svg>
              </div>
              <p className="text-gray-600">
                Entrez le code a 6 chiffres de votre application d'authentification
              </p>
            </div>
            
            <div className="mb-6">
              <input 
                type="text"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full text-center text-3xl tracking-[0.5em] border border-gray-300 rounded-lg px-4 py-4 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength="6"
                autoFocus
                data-testid="2fa-code-input"
              />
            </div>
            
            <button 
              onClick={loginWith2FA}
              disabled={loading || twoFACode.length !== 6}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 mb-4"
              data-testid="verify-2fa-btn"
            >
              {loading ? 'Verification...' : 'Verifier'}
            </button>
            
            <button 
              type="button"
              onClick={() => {
                setRequires2FA(false);
                setPending2FAUserId(null);
                setPending2FAEmail('');
                setPending2FAPassword('');
                setTwoFACode('');
              }}
              className="w-full text-gray-600 hover:text-gray-800 font-medium"
            >
              Retour a la connexion
            </button>
            
            <p className="text-center text-sm text-gray-500 mt-4">
              Vous pouvez aussi utiliser un code de secours
            </p>
          </div>
        ) : showForgotPassword ? (
          // Forgot Password Form
          <form onSubmit={handleForgotPassword}>
            <p className="text-gray-600 text-sm mb-4">
              Entrez votre email et nous vous enverrons un lien pour reinitialiser votre mot de passe.
            </p>
            <div className="mb-4">
              <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input 
                id="forgot-email"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
                data-testid="forgot-email-input"
              />
            </div>
            
            <button 
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 mb-4"
              data-testid="send-reset-btn"
            >
              {loading ? 'Envoi...' : 'Envoyer le lien'}
            </button>
            
            <button 
              type="button"
              onClick={() => setShowForgotPassword(false)}
              className="w-full text-blue-600 hover:text-blue-800 font-medium"
            >
              Retour a la connexion
            </button>
          </form>
        ) : (
          // Login/Register Form
          <>
            {/* Google Login Button */}
            <button 
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center space-x-3 border border-gray-300 rounded-lg py-3 px-4 hover:bg-gray-50 transition-colors mb-4"
              data-testid="google-login-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="text-gray-700 font-medium">Continuer avec Google</span>
            </button>

            <div className="flex items-center my-4">
              <hr className="flex-1 border-gray-300"/>
              <span className="px-4 text-gray-500 text-sm">ou</span>
              <hr className="flex-1 border-gray-300"/>
            </div>
            
            <form onSubmit={handleAuth}>
              {authMode === 'register' && (
                <div className="mb-4">
                  <label htmlFor="auth-name" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet
                  </label>
                  <input 
                    id="auth-name"
                    type="text"
                    value={authForm.name}
                    onChange={(e) => setAuthForm({...authForm, name: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              )}
              
              <div className="mb-4">
                <label htmlFor="auth-email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input 
                  id="auth-email"
                  type="email"
                  value={authForm.email}
                  onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                  data-testid="auth-email-input"
                />
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="auth-password" className="block text-sm font-medium text-gray-700">
                    Mot de passe
                  </label>
                  {authMode === 'login' && (
                    <button 
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                      data-testid="forgot-password-link"
                    >
                      Mot de passe oublie ?
                    </button>
                  )}
                </div>
                <input 
                  id="auth-password"
                  type="password"
                  value={authForm.password}
                  onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                  minLength="6"
                  data-testid="auth-password-input"
                />
              </div>
              
              <button 
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
                data-testid="auth-submit-btn"
              >
                {loading 
                  ? (authMode === 'login' ? 'Connexion...' : 'Inscription...') 
                  : (authMode === 'login' ? 'Se connecter' : "S'inscrire")
                }
              </button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                {authMode === 'login' ? "Pas encore de compte ?" : "Deja un compte ?"}
              </p>
              <button 
                onClick={() => {
                  setAuthMode(authMode === 'login' ? 'register' : 'login');
                  setAuthForm({email: '', password: '', name: ''});
                }}
                className="text-blue-600 hover:text-blue-800 font-medium"
                data-testid="switch-auth-mode"
              >
                {authMode === 'login' ? "Creer un compte" : "Se connecter"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Reset Password Modal Component
  const ResetPasswordModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="reset-password-modal">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Nouveau mot de passe</h2>
          <button 
            onClick={() => {
              setShowResetPasswordModal(false);
              setResetToken('');
              setNewPassword('');
            }}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            X
          </button>
        </div>
        
        <p className="text-gray-600 text-sm mb-4">
          Entrez votre nouveau mot de passe ci-dessous.
        </p>
        
        <form onSubmit={handleResetPassword}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nouveau mot de passe
            </label>
            <input 
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
              minLength="6"
              placeholder="Minimum 6 caracteres"
              data-testid="new-password-input"
            />
          </div>
          
          <button 
            type="submit"
            disabled={loading}
            className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
            data-testid="confirm-reset-btn"
          >
            {loading ? 'Reinitialisation...' : 'Reinitialiser le mot de passe'}
          </button>
        </form>
      </div>
    </div>
  );

  // Save Template Modal Component
  const SaveTemplateModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="save-template-modal">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Sauvegarder le Template</h2>
          <button 
            onClick={() => {
              setShowSaveTemplateModal(false);
              setTemplateName('');
            }}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            X
          </button>
        </div>
        
        <p className="text-gray-600 text-sm mb-4">
          Sauvegardez votre configuration actuelle pour la reutiliser rapidement.
        </p>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nom du Template
          </label>
          <input 
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="ex: Blog Tech E-commerce"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            data-testid="template-name-input"
          />
        </div>
        
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Configuration actuelle:</h4>
          <ul className="text-xs text-gray-600 space-y-1">
            <li><strong>Type:</strong> {contentForm.content_type}</li>
            <li><strong>Ton:</strong> {contentForm.tone}</li>
            <li><strong>Mots:</strong> {contentForm.word_count}</li>
            {contentForm.industry && <li><strong>Secteur:</strong> {contentForm.industry}</li>}
            {contentForm.topic && <li><strong>Sujet:</strong> {contentForm.topic.substring(0, 50)}...</li>}
          </ul>
        </div>
        
        <div className="flex space-x-3">
          <button 
            onClick={() => {
              setShowSaveTemplateModal(false);
              setTemplateName('');
            }}
            className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors"
          >
            Annuler
          </button>
          <button 
            onClick={saveTemplate}
            className="flex-1 bg-green-600 text-white py-2 rounded-lg font-medium hover:bg-green-700 transition-colors"
            data-testid="confirm-save-template-btn"
          >
            Sauvegarder
          </button>
        </div>
      </div>
    </div>
  );

  // 2FA Setup Modal Component
  const TwoFASetupModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="2fa-setup-modal">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">Configuration 2FA</h2>
          <button 
            onClick={() => {
              setShow2FASetup(false);
              setTwoFactorSecret('');
              setTwoFactorQRUri('');
              setTwoFactorQRImage('');
              setTwoFACode('');
            }}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            X
          </button>
        </div>
        
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </div>
          <p className="text-gray-600 text-sm">
            Scannez ce QR code avec votre application d'authentification<br/>
            (Google Authenticator, Authy, etc.)
          </p>
        </div>
        
        {/* QR Code Image */}
        <div className="bg-gray-100 rounded-lg p-4 mb-4">
          {twoFactorQRImage ? (
            <div className="flex flex-col items-center">
              <img 
                src={twoFactorQRImage} 
                alt="QR Code 2FA" 
                className="w-48 h-48 mx-auto rounded-lg shadow-md"
                data-testid="2fa-qr-code"
              />
              <p className="text-xs text-gray-500 mt-3">Scannez avec votre app d'authentification</p>
            </div>
          ) : (
            <div className="bg-white p-4 rounded-lg text-center">
              <p className="text-xs text-gray-500 mb-2">URI pour QR Code:</p>
              <p className="text-xs text-gray-700 break-all font-mono">{twoFactorQRUri}</p>
            </div>
          )}
          <div className="mt-4 pt-3 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500 mb-1">Ou entrez ce code manuellement:</p>
            <p className="font-mono text-lg font-bold tracking-widest text-gray-800 bg-white py-2 px-4 rounded-lg inline-block">{twoFactorSecret}</p>
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Code de verification
          </label>
          <input 
            type="text"
            value={twoFACode}
            onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            className="w-full text-center text-2xl tracking-[0.3em] border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            maxLength="6"
            data-testid="2fa-setup-code-input"
          />
        </div>
        
        <button 
          onClick={verify2FASetup}
          disabled={loading || twoFACode.length !== 6}
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
          data-testid="verify-2fa-setup-btn"
        >
          {loading ? 'Verification...' : 'Activer 2FA'}
        </button>
      </div>
    </div>
  );

  // Backup Codes Modal Component
  const BackupCodesModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="backup-codes-modal">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">Codes de secours</h2>
          <button 
            onClick={() => {
              setShowBackupCodes(false);
              setBackupCodes([]);
            }}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            X
          </button>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
            </svg>
            <p className="text-sm text-yellow-800">
              Conservez ces codes dans un endroit sur. Chaque code ne peut etre utilise qu'une seule fois.
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-2 mb-6">
          {backupCodes.map((code, index) => (
            <div key={index} className="bg-gray-100 rounded-lg p-3 text-center">
              <code className="font-mono text-lg font-bold text-gray-800">{code}</code>
            </div>
          ))}
        </div>
        
        {backupCodes.length > 0 && (
          <p className="text-center text-sm text-gray-500 mb-4">
            {backupCodes.length} codes restants
          </p>
        )}
        
        <div className="flex space-x-3">
          <button 
            onClick={() => {
              const codesText = backupCodes.join('\n');
              navigator.clipboard.writeText(codesText);
              alert('Codes copies dans le presse-papier !');
            }}
            className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            data-testid="copy-backup-codes-btn"
          >
            Copier
          </button>
          <button 
            onClick={regenerateBackupCodes}
            className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            data-testid="regenerate-backup-codes-btn"
          >
            Regenerer
          </button>
        </div>
      </div>
    </div>
  );

  // Home Page Component
  const HomePage = () => (
    <div className="min-h-screen vf-gradient-bg">
      <header className="vf-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setCurrentPage('home')}>
              <img src="/vfa-logo.png" alt="VF Actions" className="h-10 w-auto" />
            </div>
            <nav className="flex space-x-2 items-center">
              {user ? (
                <>
                  <button 
                    onClick={() => setCurrentPage('pro')}
                    className="nav-link bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg font-medium hover:from-green-600 hover:to-emerald-700 transition-all"
                    data-testid="nav-pro"
                  >
                    🚀 PRO
                  </button>
                  <button 
                    onClick={() => setCurrentPage('dashboard')}
                    className="nav-link"
                    data-testid="nav-dashboard"
                  >
                    {t('nav.dashboard')}
                  </button>
                  <button 
                    onClick={() => setCurrentPage('actualites')}
                    className="nav-link"
                    data-testid="nav-actualites"
                  >
                    {t('nav.actualites')}
                  </button>
                  <button 
                    onClick={() => setCurrentPage('pricing')}
                    className="nav-link"
                    data-testid="nav-pricing"
                  >
                    {t('nav.pricing')}
                  </button>
                  {isAdmin && (
                    <button 
                      onClick={() => setCurrentPage('admin')}
                      className="nav-link bg-red-500 text-white px-4 py-2 rounded"
                      data-testid="nav-admin"
                    >
                      {t('nav.admin')}
                    </button>
                  )}
                  <LanguageSelector />
                  <div className="flex items-center space-x-4 ml-4 pl-4 border-l border-gray-700">
                    <span className="text-sm text-gray-600" data-testid="user-name">{user.name}</span>
                    <button 
                      onClick={handleLogout}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                      data-testid="logout-btn"
                    >
                      {t('nav.logout')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button 
                    onClick={() => setCurrentPage('actualites')}
                    className="nav-link"
                    data-testid="nav-actualites-guest"
                  >
                    {t('nav.actualites')}
                  </button>
                  <button 
                    onClick={() => setCurrentPage('pricing')}
                    className="nav-link"
                    data-testid="nav-pricing-guest"
                  >
                    {t('nav.pricing')}
                  </button>
                  <LanguageSelector />
                  <button 
                    onClick={() => {
                      setShowAuthModal(true);
                      setAuthMode('login');
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded font-medium hover:bg-blue-700"
                    data-testid="login-btn"
                  >
                    {t('nav.login')}
                  </button>
                  <button 
                    onClick={() => {
                      setShowAuthModal(true);
                      setAuthMode('register');
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded font-medium hover:bg-green-700"
                    data-testid="register-btn"
                  >
                    {t('nav.register')}
                  </button>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* HERO SECTION - Design dynamique restauré */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Video/Image Background with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="/hero-bg.jpg" 
            alt="" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19]/90 via-[#0B0F19]/70 to-[#0B0F19]"></div>
        </div>

        {/* Animated Particles Effect */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-green-400 rounded-full animate-ping" style={{animationDuration: '3s'}}></div>
          <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-emerald-400 rounded-full animate-ping" style={{animationDuration: '2s', animationDelay: '0.5s'}}></div>
          <div className="absolute bottom-1/3 left-1/3 w-1.5 h-1.5 bg-teal-400 rounded-full animate-ping" style={{animationDuration: '2.5s', animationDelay: '1s'}}></div>
          <div className="absolute top-1/2 right-1/4 w-1 h-1 bg-cyan-400 rounded-full animate-ping" style={{animationDuration: '3.5s', animationDelay: '1.5s'}}></div>
          {/* Glowing orbs */}
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-green-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-emerald-500/15 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
        </div>

        <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">
          {/* Logo with glow effect */}
          <div className="mb-8 animate-fade-in-up">
            <div className="inline-block relative">
              <div className="absolute inset-0 bg-green-500/30 blur-2xl rounded-full scale-150"></div>
              <img src="/vfa-logo.png" alt="VF Actions" className="h-24 md:h-28 relative" />
            </div>
          </div>

          {/* Badge - Positionnement territorial discret */}
          <div className="mb-6 animate-fade-in-up" style={{animationDelay: '0.1s'}}>
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30 text-green-400 text-sm font-medium backdrop-blur-sm">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
              {t('hero.aiService')}
            </span>
          </div>

          {/* Main Headline - Ancien style poétique */}
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in-up" style={{animationDelay: '0.2s'}}>
            <span className="text-white">{t('hero.heroTitle1')}</span>
            <br />
            <span className="bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 bg-clip-text text-transparent">
              {t('hero.heroTitle2')}</span>
          </h1>

          {/* Subtitle - Refined */}
          <p className="text-xl md:text-2xl text-gray-400 mb-10 max-w-3xl mx-auto leading-relaxed animate-fade-in-up" style={{animationDelay: '0.3s'}}>
            {t('hero.heroSubtitle')}
            <br className="hidden md:block" />
            <span className="text-gray-500">{t('hero.heroSubtitleAccent')}</span>
          </p>

          {/* Stats - Discrete */}
          <div className="flex flex-wrap justify-center gap-8 mb-10 animate-fade-in-up" style={{animationDelay: '0.4s'}}>
            <div className="flex items-center space-x-2 text-gray-500 text-sm">
              <span className="w-1.5 h-1.5 bg-green-400/50 rounded-full"></span>
              <span className="text-gray-400">2,340 {t('hero.statCreations')}</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-500 text-sm">
              <span className="w-1.5 h-1.5 bg-emerald-400/50 rounded-full"></span>
              <span className="text-gray-400">156 {t('hero.statTrees')}</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-500 text-sm">
              <span className="w-1.5 h-1.5 bg-teal-400/50 rounded-full"></span>
              <span className="text-gray-400">98% {t('hero.statSatisfied')}</span>
            </div>
          </div>

          {/* CTA Buttons - Avec emojis restaurés */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-16 animate-fade-in-up" style={{animationDelay: '0.5s'}}>
            <button 
              onClick={() => {
                if (user) {
                  setCurrentPage('pro');
                } else {
                  setCurrentPage('wizard');
                }
              }}
              className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-medium text-lg transition-all hover:scale-[1.02] shadow-lg shadow-green-500/30"
              data-testid="cta-start"
            >
              {user ? t('hero.ctaDashboard') : t('hero.ctaCreateStrategy')}
            </button>
            {user && (
              <button 
                onClick={() => setCurrentPage('wizard')}
                className="px-8 py-4 bg-transparent border border-green-600 rounded-xl font-medium text-green-400 text-lg hover:bg-green-600/10 transition-all"
                data-testid="cta-new-strategy"
              >
                {t('hero.ctaNewStrategy')}
              </button>
            )}
            {user && (
              <button 
                onClick={() => setCurrentPage('projects')}
                className="px-8 py-4 bg-gray-800 hover:bg-gray-700 rounded-xl font-medium text-white text-lg transition-all flex items-center space-x-2"
                data-testid="cta-my-projects"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                </svg>
                <span>{t('hero.ctaMyProjects')}</span>
              </button>
            )}
            {!user && (
              <button 
                onClick={() => setCurrentPage('landing')}
                className="px-8 py-4 bg-transparent border border-gray-700 rounded-xl font-medium text-gray-300 text-lg hover:border-gray-500 hover:text-white transition-all"
                data-testid="cta-discover"
              >
                Découvrir la nouvelle version
              </button>
            )}
          </div>

          {/* Scroll Indicator */}
          <div className="animate-bounce opacity-50">
            <svg className="w-5 h-5 mx-auto text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
            </svg>
          </div>
        </div>
      </section>

      {/* FEATURES SECTION with Images - Design restauré */}
      <section className="py-20 bg-[#0B0F19] relative">
        <div className="max-w-7xl mx-auto px-4">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-green-400 text-xs font-mono uppercase tracking-wider">{t('features.sectionLabel')}</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-4">{t('features.sectionTitle')}</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">{t('features.sectionSubtitle')}</p>
          </div>

          {/* Features Grid with Images */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            {/* Feature 1 - AI Brain */}
            <div className="group relative rounded-2xl overflow-hidden bg-gray-900/50 border border-gray-800 hover:border-green-500/50 transition-all">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2 p-8 relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{t('features.aiTitle')}</h3>
                  <p className="text-gray-400 mb-4">{t('features.aiDesc')}</p>
                  <ul className="space-y-2 text-sm text-gray-500">
                    <li className="flex items-center"><span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-2"></span>{t('features.aiFeature1')}</li>
                    <li className="flex items-center"><span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-2"></span>{t('features.aiFeature2')}</li>
                    <li className="flex items-center"><span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-2"></span>{t('features.aiFeature3')}</li>
                  </ul>
                </div>
                <div className="md:w-1/2 relative h-64 md:h-auto">
                  <img src="/ai-brain.jpg" alt="IA Marketing" className="absolute inset-0 w-full h-full object-cover"/>
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-900 to-transparent"></div>
                </div>
              </div>
            </div>

            {/* Feature 2 - Engagement Responsable */}
            <div className="group relative rounded-2xl overflow-hidden bg-gray-900/50 border border-gray-800 hover:border-emerald-500/50 transition-all">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2 p-8 relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{t('features.ecoTitle')}</h3>
                  <p className="text-gray-400 mb-4">{t('features.ecoDesc')}</p>
                  <ul className="space-y-2 text-sm text-gray-500">
                    <li className="flex items-center"><span className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-2"></span>{t('features.ecoFeature1')}</li>
                    <li className="flex items-center"><span className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-2"></span>{t('features.ecoFeature2')}</li>
                    <li className="flex items-center"><span className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-2"></span>{t('features.ecoFeature3')}</li>
                  </ul>
                </div>
                <div className="md:w-1/2 relative h-64 md:h-auto">
                  <img src="/eco-bulb.jpg" alt="Engagement Responsable" className="absolute inset-0 w-full h-full object-cover"/>
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-900 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Banner */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-6 glass-card group hover:border-green-500/50 transition-all">
              <p className="text-4xl font-bold text-green-400 font-mono group-hover:scale-110 transition-transform">5+</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider mt-2">{t('stats.contentTypes')}</p>
              <p className="text-gray-400 text-xs mt-1">{t('stats.contentTypesDesc')}</p>
            </div>
            <div className="text-center p-6 glass-card group hover:border-emerald-500/50 transition-all">
              <p className="text-4xl font-bold text-emerald-400 font-mono group-hover:scale-110 transition-transform">100%</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider mt-2">{t('stats.madeInFrance')}</p>
              <p className="text-gray-400 text-xs mt-1">{t('stats.madeInFranceDesc')}</p>
            </div>
            <div className="text-center p-6 glass-card group hover:border-teal-500/50 transition-all">
              <p className="text-4xl font-bold text-teal-400 font-mono group-hover:scale-110 transition-transform">IA</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider mt-2">{t('stats.aiGeneration')}</p>
              <p className="text-gray-400 text-xs mt-1">{t('stats.aiGenerationDesc')}</p>
            </div>
            <div className="text-center p-6 glass-card group hover:border-cyan-500/50 transition-all">
              <p className="text-4xl font-bold text-cyan-400 font-mono group-hover:scale-110 transition-transform">24h</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider mt-2">{t('stats.support')}</p>
              <p className="text-gray-400 text-xs mt-1">{t('stats.supportDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* TRANSPARENCE & CONFIANCE - Design restauré */}
      <section className="py-20 bg-gradient-to-b from-[#0B0F19] to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-green-500/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <span className="text-green-400 text-xs font-mono uppercase tracking-wider">02 — {t('transparency.sectionTitle')}</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-4">{t('transparency.title')}</h2>
            <p className="text-gray-400 text-lg">{t('transparency.subtitle')}</p>
          </div>

          {/* Honest Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-green-500/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <p className="text-3xl font-bold text-white font-mono mb-2">100%</p>
              <p className="text-gray-400 text-sm">{t('transparency.realData')}</p>
              <p className="text-gray-500 text-xs mt-2">{t('transparency.realDataDesc')}</p>
            </div>
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                </svg>
              </div>
              <p className="text-3xl font-bold text-white font-mono mb-2">RGPD</p>
              <p className="text-gray-400 text-sm">{t('transparency.rgpd')}</p>
              <p className="text-gray-500 text-xs mt-2">{t('transparency.rgpdDesc')}</p>
            </div>
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-teal-500/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064"/>
                </svg>
              </div>
              <p className="text-3xl font-bold text-white font-mono mb-2">{t('transparency.verified')}</p>
              <p className="text-gray-400 text-sm">{t('transparency.verifiedDesc')}</p>
              <p className="text-gray-500 text-xs mt-2">{t('transparency.verifiedPartner')}</p>
            </div>
          </div>

          {/* Honest Message */}
          <div className="bg-gray-900/30 border border-gray-800 rounded-2xl p-8 text-center">
            <p className="text-gray-300 text-lg leading-relaxed mb-4">
              {t('transparency.message')} <span className="text-green-400">{t('transparency.transparency')}</span> {t('transparency.and')} <span className="text-emerald-400">{t('transparency.authenticity')}</span>.
            </p>
            <p className="text-gray-500 text-sm">
              {t('transparency.testimonialNote')}
            </p>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <button 
              onClick={() => {
                if (user) {
                  setCurrentPage('dashboard');
                } else {
                  setShowAuthModal(true);
                  setAuthMode('register');
                }
              }}
              className="px-8 py-4 bg-white text-gray-900 rounded-xl font-medium text-lg hover:bg-gray-100 transition-all"
            >
              {t('transparency.ctaButton')}
            </button>
            <p className="text-gray-500 text-sm mt-4">{t('transparency.ctaSubtext')}</p>
          </div>
        </div>
      </section>

      {/* FOOTER - Design restauré */}
      <footer className="bg-gray-900 border-t border-gray-800 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <img src="/vfa-logo.png" alt="VF Actions" className="h-10" />
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                {t('footer.tagline')}<br />
                {t('footer.description')}
              </p>
              <p className="text-gray-500 text-xs">
                {t('footer.badges')}
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="text-white font-medium mb-4">{t('footer.navTitle')}</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => setCurrentPage('home')} className="text-gray-400 hover:text-green-400 transition-colors">{t('footer.navHome')}</button></li>
                <li><button onClick={() => setCurrentPage('notre-approche')} className="text-gray-400 hover:text-green-400 transition-colors">Notre Approche</button></li>
                <li><button onClick={() => setCurrentPage('pricing')} className="text-gray-400 hover:text-green-400 transition-colors">{t('footer.navPricing')}</button></li>
                <li><button onClick={() => setCurrentPage('actualites')} className="text-gray-400 hover:text-green-400 transition-colors">{t('footer.navNews')}</button></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-white font-medium mb-4">{t('footer.legalTitle')}</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => setCurrentPage('mentions-legales')} className="text-gray-400 hover:text-green-400 transition-colors">{t('footer.legalMentions')}</button></li>
                <li><button onClick={() => setCurrentPage('cgu')} className="text-gray-400 hover:text-green-400 transition-colors">{t('footer.legalCGU')}</button></li>
                <li><button onClick={() => setCurrentPage('confidentialite')} className="text-gray-400 hover:text-green-400 transition-colors">{t('footer.legalPrivacy')}</button></li>
                <li><button onClick={() => setCurrentPage('rgpd')} className="text-gray-400 hover:text-green-400 transition-colors">{t('footer.legalRGPD')}</button></li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} VF Actions. {t('footer.rights')}.
            </p>
            <div className="flex items-center space-x-4 mt-4 md:mt-0">
              <a href="https://vfactions.eu" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-green-400 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>
              </a>
              <a href="mailto:contact@vfactions.eu" className="text-gray-400 hover:text-green-400 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );

  // PAGES LÉGALES

  // Mentions Légales
  const MentionsLegales = () => (
    <div className="min-h-screen bg-[#0B0F19]">
      <header className="bg-[#0B0F19]/90 backdrop-blur-xl border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setCurrentPage('home')}>
            <img src="/vfa-logo.png" alt="VF Actions" className="h-8" />
          </div>
          <button onClick={() => setCurrentPage('home')} className="text-gray-400 hover:text-green-400 transition-colors">
            ← Retour
          </button>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-4 py-16">
        <h1 className="text-4xl font-bold text-white mb-8">Mentions Légales</h1>
        <div className="prose prose-invert prose-green max-w-none">
          <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">1. Éditeur du site</h2>
              <p className="text-gray-400">
                Le site VF Actions est édité par :<br/>
                <strong className="text-white">VF ACTIONS</strong><br/>
                Adresse : France<br/>
                Email : contact@vfactions.eu<br/>
                Site web : vfactions.eu
              </p>
            </section>
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">2. Hébergement</h2>
              <p className="text-gray-400">
                Ce site est hébergé par des services cloud éco-responsables, 
                dans le respect de notre engagement environnemental.
              </p>
            </section>
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">3. Propriété intellectuelle</h2>
              <p className="text-gray-400">
                L'ensemble du contenu de ce site (textes, images, logos, design) est protégé 
                par les droits de propriété intellectuelle. Toute reproduction, même partielle, 
                est interdite sans autorisation préalable.
              </p>
            </section>
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">4. Responsabilité</h2>
              <p className="text-gray-400">
                VF Actions s'efforce de fournir des informations exactes et à jour. 
                Toutefois, nous ne pouvons garantir l'exactitude de toutes les informations 
                présentes sur le site.
              </p>
            </section>
          </div>
        </div>
        <p className="text-gray-500 text-sm mt-8">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
      </div>
    </div>
  );

  // Conditions Générales d'Utilisation
  const CGU = () => (
    <div className="min-h-screen bg-[#0B0F19]">
      <header className="bg-[#0B0F19]/90 backdrop-blur-xl border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setCurrentPage('home')}>
            <img src="/vfa-logo.png" alt="VF Actions" className="h-8" />
          </div>
          <button onClick={() => setCurrentPage('home')} className="text-gray-400 hover:text-green-400 transition-colors">
            ← Retour
          </button>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-4 py-16">
        <h1 className="text-4xl font-bold text-white mb-8">Conditions Générales d'Utilisation</h1>
        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 space-y-6">
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Objet</h2>
            <p className="text-gray-400">
              Les présentes CGU régissent l'utilisation du service VF Actions, 
              une plateforme de génération de contenu marketing assistée par intelligence artificielle.
            </p>
          </section>
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Inscription et compte</h2>
            <p className="text-gray-400">
              L'accès au service nécessite la création d'un compte. L'utilisateur s'engage à fournir 
              des informations exactes et à maintenir la confidentialité de ses identifiants.
            </p>
          </section>
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. Utilisation du service</h2>
            <p className="text-gray-400">
              Le contenu généré par VF Actions est destiné à un usage professionnel. 
              L'utilisateur est responsable de la vérification et de l'adaptation du contenu généré.
              Il est interdit d'utiliser le service pour générer du contenu illégal, diffamatoire ou trompeur.
            </p>
          </section>
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Crédits et facturation</h2>
            <p className="text-gray-400">
              L'utilisation du service fonctionne avec un système de crédits. Les crédits achetés 
              ne sont ni remboursables ni transférables. Les tarifs peuvent être modifiés avec un 
              préavis de 30 jours.
            </p>
          </section>
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Propriété du contenu</h2>
            <p className="text-gray-400">
              Le contenu généré par l'utilisateur via VF Actions lui appartient. VF Actions 
              conserve les droits sur sa technologie, ses algorithmes et son interface.
            </p>
          </section>
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Limitation de responsabilité</h2>
            <p className="text-gray-400">
              VF Actions ne peut être tenu responsable des dommages directs ou indirects 
              résultant de l'utilisation du service. Le contenu généré par IA doit être 
              vérifié avant publication.
            </p>
          </section>
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">7. Modification des CGU</h2>
            <p className="text-gray-400">
              VF Actions se réserve le droit de modifier les présentes CGU. Les utilisateurs 
              seront informés de toute modification par email.
            </p>
          </section>
        </div>
        <p className="text-gray-500 text-sm mt-8">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
      </div>
    </div>
  );

  // Politique de Confidentialité
  const Confidentialite = () => (
    <div className="min-h-screen bg-[#0B0F19]">
      <header className="bg-[#0B0F19]/90 backdrop-blur-xl border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setCurrentPage('home')}>
            <img src="/vfa-logo.png" alt="VF Actions" className="h-8" />
          </div>
          <button onClick={() => setCurrentPage('home')} className="text-gray-400 hover:text-green-400 transition-colors">
            ← Retour
          </button>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-4 py-16">
        <h1 className="text-4xl font-bold text-white mb-8">Politique de Confidentialité</h1>
        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 space-y-6">
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Données collectées</h2>
            <p className="text-gray-400">
              Nous collectons les données suivantes :<br/>
              • <strong className="text-gray-300">Données d'identification</strong> : nom, email, mot de passe (hashé)<br/>
              • <strong className="text-gray-300">Données d'utilisation</strong> : contenus générés, historique, préférences<br/>
              • <strong className="text-gray-300">Données techniques</strong> : adresse IP, type de navigateur
            </p>
          </section>
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Finalités du traitement</h2>
            <p className="text-gray-400">
              Vos données sont utilisées pour :<br/>
              • Fournir et améliorer le service<br/>
              • Gérer votre compte et vos abonnements<br/>
              • Communiquer avec vous (support, mises à jour)<br/>
              • Assurer la sécurité du service
            </p>
          </section>
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. Conservation des données</h2>
            <p className="text-gray-400">
              Vos données sont conservées pendant la durée de votre utilisation du service, 
              puis supprimées dans un délai de 3 ans après la clôture de votre compte, 
              sauf obligation légale contraire.
            </p>
          </section>
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Partage des données</h2>
            <p className="text-gray-400">
              Nous ne vendons jamais vos données. Elles peuvent être partagées avec :<br/>
              • Nos prestataires techniques (hébergement, paiement)<br/>
              • Les autorités si requis par la loi
            </p>
          </section>
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Sécurité</h2>
            <p className="text-gray-400">
              Nous mettons en œuvre des mesures de sécurité appropriées : chiffrement SSL, 
              hashage des mots de passe, accès restreint aux données.
            </p>
          </section>
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Cookies</h2>
            <p className="text-gray-400">
              Nous utilisons des cookies essentiels au fonctionnement du service 
              (authentification, préférences). Aucun cookie de tracking publicitaire n'est utilisé.
            </p>
          </section>
        </div>
        <p className="text-gray-500 text-sm mt-8">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
      </div>
    </div>
  );

  // RGPD - Droits des utilisateurs
  const RGPD = () => (
    <div className="min-h-screen bg-[#0B0F19]">
      <header className="bg-[#0B0F19]/90 backdrop-blur-xl border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setCurrentPage('home')}>
            <img src="/vfa-logo.png" alt="VF Actions" className="h-8" />
          </div>
          <button onClick={() => setCurrentPage('home')} className="text-gray-400 hover:text-green-400 transition-colors">
            ← Retour
          </button>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-4 py-16">
        <h1 className="text-4xl font-bold text-white mb-4">RGPD</h1>
        <p className="text-gray-400 text-lg mb-8">Règlement Général sur la Protection des Données</p>
        
        <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-2xl p-6 mb-8">
          <div className="flex items-center space-x-3 mb-3">
            <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
            <h3 className="text-white font-semibold">VF Actions est conforme au RGPD</h3>
          </div>
          <p className="text-gray-400 text-sm">
            Nous respectons vos droits et protégeons vos données personnelles conformément 
            au Règlement Européen 2016/679.
          </p>
        </div>

        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 space-y-6">
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">Vos droits</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-800/50 rounded-xl p-4">
                <h3 className="text-green-400 font-medium mb-2">Droit d'accès</h3>
                <p className="text-gray-400 text-sm">Obtenir une copie de vos données personnelles</p>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-4">
                <h3 className="text-green-400 font-medium mb-2">Droit de rectification</h3>
                <p className="text-gray-400 text-sm">Corriger vos données inexactes ou incomplètes</p>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-4">
                <h3 className="text-green-400 font-medium mb-2">Droit à l'effacement</h3>
                <p className="text-gray-400 text-sm">Demander la suppression de vos données</p>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-4">
                <h3 className="text-green-400 font-medium mb-2">Droit à la portabilité</h3>
                <p className="text-gray-400 text-sm">Récupérer vos données dans un format standard</p>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-4">
                <h3 className="text-green-400 font-medium mb-2">Droit d'opposition</h3>
                <p className="text-gray-400 text-sm">Vous opposer au traitement de vos données</p>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-4">
                <h3 className="text-green-400 font-medium mb-2">Droit à la limitation</h3>
                <p className="text-gray-400 text-sm">Limiter le traitement de vos données</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">Comment exercer vos droits</h2>
            <p className="text-gray-400 mb-4">
              Pour exercer vos droits, contactez-nous à :
            </p>
            <div className="bg-gray-800/50 rounded-xl p-4 flex items-center space-x-3">
              <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              <a href="mailto:rgpd@vfactions.eu" className="text-green-400 hover:underline">rgpd@vfactions.eu</a>
            </div>
            <p className="text-gray-500 text-sm mt-4">
              Nous répondrons à votre demande dans un délai de 30 jours maximum.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">Autorité de contrôle</h2>
            <p className="text-gray-400">
              En cas de litige, vous pouvez introduire une réclamation auprès de la CNIL :<br/>
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:underline">www.cnil.fr</a>
            </p>
          </section>
        </div>
        <p className="text-gray-500 text-sm mt-8">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
      </div>
    </div>
  );

  // Dashboard Component - Theme vfactions.eu (Dark, Real-time stats, Intuitive)
  const Dashboard = () => {
    // Calculate live stats from existing state
    const liveStats = {
      contentsToday: userContent.filter(c => {
        const today = new Date().toDateString();
        return new Date(c.created_at).toDateString() === today;
      }).length,
      creditsUsed: user?.credits_used || 0,
      avgTime: 3,
      satisfaction: 98
    };

    return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="dashboard">
      {/* Ambient effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-green-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <header className="bg-[#0B0F19]/90 backdrop-blur-xl border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setCurrentPage('home')}>
              <img src="/vfa-logo.png" alt="VF Actions" className="h-8 w-auto" />
            </div>
            <nav className="flex space-x-4 items-center">
              <button onClick={() => setCurrentPage('home')} className="text-gray-400 hover:text-green-400 font-medium text-sm transition-colors">Home</button>
              <button onClick={() => setCurrentPage('actualites')} className="text-gray-400 hover:text-green-400 font-medium text-sm transition-colors">{t('nav.actualites')}</button>
              <button onClick={() => setCurrentPage('pricing')} className="text-gray-400 hover:text-green-400 font-medium text-sm transition-colors">{t('nav.pricing')}</button>
              {isAdmin && (
                <button onClick={() => setCurrentPage('admin')} className="bg-red-500/20 text-red-400 px-3 py-1.5 rounded-lg font-medium text-sm hover:bg-red-500/30 transition-colors">Admin</button>
              )}
              <LanguageSelector />
              <div className="flex items-center space-x-3 ml-4 pl-4 border-l border-gray-700">
                <span className="text-sm text-gray-400">{user?.name}</span>
                <button onClick={handleLogout} className="text-red-400 hover:text-red-300 text-sm font-medium transition-colors">{t('nav.logout')}</button>
              </div>
            </nav>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Welcome Section with Live Stats */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-green-400 text-xs font-mono uppercase tracking-wider">01</span>
            <span className="text-gray-600 text-xs">•</span>
            <span className="text-gray-500 text-xs uppercase tracking-wider">{t('nav.dashboard')}</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('dashboard.welcome')}, {user?.name?.split(' ')[0]} !</h1>
          <p className="text-gray-400">Créez du contenu marketing professionnel en quelques secondes.</p>
        </div>

        {/* Real-time Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-16 h-16 bg-green-500/10 rounded-full blur-2xl group-hover:bg-green-500/20 transition-all"></div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-500 text-xs uppercase tracking-wider">{t('dashboard.credits')}</span>
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            </div>
            <p className="text-3xl font-bold text-white font-mono">{user?.credits_limit === -1 ? '∞' : (user?.credits_limit - (user?.credits_used || 0))}</p>
            <p className="text-gray-500 text-xs mt-1">disponibles</p>
          </div>
          <div className="glass-card p-5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500/10 rounded-full blur-2xl group-hover:bg-emerald-500/20 transition-all"></div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-500 text-xs uppercase tracking-wider">Aujourd'hui</span>
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
            </div>
            <p className="text-3xl font-bold text-white font-mono">{liveStats.contentsToday}</p>
            <p className="text-gray-500 text-xs mt-1">contenus créés</p>
          </div>
          <div className="glass-card p-5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-16 h-16 bg-teal-500/10 rounded-full blur-2xl group-hover:bg-teal-500/20 transition-all"></div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-500 text-xs uppercase tracking-wider">Temps moyen</span>
              <span className="w-2 h-2 bg-teal-400 rounded-full animate-pulse"></span>
            </div>
            <p className="text-3xl font-bold text-white font-mono">{liveStats.avgTime}s</p>
            <p className="text-gray-500 text-xs mt-1">par génération</p>
          </div>
          <div className="glass-card p-5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-16 h-16 bg-cyan-500/10 rounded-full blur-2xl group-hover:bg-cyan-500/20 transition-all"></div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-500 text-xs uppercase tracking-wider">{t('dashboard.plan')}</span>
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
            </div>
            <p className="text-2xl font-bold text-white capitalize">{user?.subscription_plan || 'Starter'}</p>
            <p className="text-gray-500 text-xs mt-1">plan actif</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Content Generation Form */}
          <div className="lg:col-span-2">
            <div className="glass-card p-6">
              <div className="flex items-center space-x-2 mb-6">
                <span className="text-green-400 text-xs font-mono uppercase tracking-wider">02</span>
                <span className="text-gray-600 text-xs">•</span>
                <span className="text-gray-500 text-xs uppercase tracking-wider">{t('dashboard.generate')}</span>
              </div>
              
              <div className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">{t('dashboard.contentType')}</label>
                    <select 
                      value={contentForm.content_type}
                      onChange={(e) => setContentForm({...contentForm, content_type: e.target.value})}
                      className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all"
                      data-testid="content-type-select"
                    >
                      <option value="blog">{t('contentTypes.blog')}</option>
                      <option value="social">{t('contentTypes.social')}</option>
                      <option value="email">{t('contentTypes.email')}</option>
                      <option value="ad">{t('contentTypes.ad')}</option>
                      <option value="description">{t('contentTypes.description')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">{t('dashboard.industry')}</label>
                    <select 
                      value={contentForm.industry}
                      onChange={(e) => setContentForm({...contentForm, industry: e.target.value})}
                      className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all"
                      data-testid="industry-select"
                    >
                      {industries.map(ind => (
                        <option key={ind.id} value={ind.id}>{ind.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('dashboard.topic')}</label>
                  <FormInput 
                    type="text"
                    value={contentForm.topic}
                    onChange={(val) => setContentForm({...contentForm, topic: val})}
                    placeholder="Ex: Les avantages de notre produit..."
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all placeholder-gray-500"
                    data-testid="topic-input"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">{t('dashboard.tone')}</label>
                    <select 
                      value={contentForm.tone}
                      onChange={(e) => setContentForm({...contentForm, tone: e.target.value})}
                      className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all"
                      data-testid="tone-select"
                    >
                      <option value="professional">{t('tones.professional')}</option>
                      <option value="casual">{t('tones.casual')}</option>
                      <option value="formal">{t('tones.formal')}</option>
                      <option value="friendly">{t('tones.friendly')}</option>
                      <option value="persuasive">{t('tones.persuasive')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">{t('dashboard.wordCount')}</label>
                    <FormInput 
                      type="number"
                      value={contentForm.word_count}
                      onChange={(val) => setContentForm({...contentForm, word_count: parseInt(val) || 500})}
                      min="50"
                      max="2000"
                      className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all"
                      data-testid="word-count-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('dashboard.instructions')}</label>
                  <FormTextarea 
                    value={contentForm.additional_instructions}
                    onChange={(val) => setContentForm({...contentForm, additional_instructions: val})}
                    rows="3"
                    placeholder="Instructions spécifiques pour personnaliser le contenu..."
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all placeholder-gray-500 resize-none"
                    data-testid="instructions-textarea"
                  />
                </div>

                <button 
                  onClick={generateContent}
                  disabled={loading || !contentForm.topic.trim()}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:from-gray-600 disabled:to-gray-700 text-white py-4 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 shadow-lg shadow-green-500/30 disabled:shadow-none"
                  data-testid="generate-btn"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{t('dashboard.generating')}</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                      </svg>
                      <span>{t('dashboard.generateBtn')}</span>
                    </>
                  )}
                </button>
              </div>

              {/* Generated Content Display */}
              {generatedContent && (
                <div className="mt-6 pt-6 border-t border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-green-400 text-xs font-mono uppercase tracking-wider">03</span>
                      <span className="text-gray-600 text-xs">•</span>
                      <span className="text-gray-500 text-xs uppercase tracking-wider">{t('dashboard.result')}</span>
                    </div>
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => navigator.clipboard.writeText(generatedContent)}
                        className="text-gray-400 hover:text-green-400 p-2 rounded-lg hover:bg-gray-800 transition-all"
                        title={t('dashboard.copy')}
                        data-testid="copy-btn"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                        </svg>
                      </button>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(generatedContent);
                          alert('Contenu sauvegardé dans le presse-papier !');
                        }}
                        className="text-gray-400 hover:text-green-400 p-2 rounded-lg hover:bg-gray-800 transition-all"
                        title={t('dashboard.save')}
                        data-testid="save-btn"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="bg-gray-800/50 rounded-xl p-5 prose prose-invert max-w-none" data-testid="generated-content">
                    <pre className="whitespace-pre-wrap text-gray-300 text-sm leading-relaxed font-sans">{generatedContent}</pre>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - History & Templates */}
          <div className="space-y-6">
            {/* Recent History */}
            <div className="glass-card p-5">
              <div className="flex items-center space-x-2 mb-4">
                <span className="text-green-400 text-xs font-mono uppercase tracking-wider">04</span>
                <span className="text-gray-600 text-xs">•</span>
                <span className="text-gray-500 text-xs uppercase tracking-wider">{t('dashboard.history')}</span>
              </div>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {userContent.slice(0, 5).map((item, index) => (
                  <div 
                    key={item.id || index}
                    className="p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 cursor-pointer transition-all group"
                    onClick={() => setGeneratedContent(item.content)}
                    data-testid={`history-item-${index}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-green-400 font-medium uppercase">{item.content_type}</span>
                      <span className="text-xs text-gray-500">{new Date(item.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                    <p className="text-sm text-gray-300 truncate group-hover:text-white transition-colors">{item.topic}</p>
                  </div>
                ))}
                {userContent.length === 0 && (
                  <p className="text-gray-500 text-sm text-center py-4">Aucun historique</p>
                )}
              </div>
            </div>

            {/* Quick Templates */}
            <div className="glass-card p-5">
              <div className="flex items-center space-x-2 mb-4">
                <span className="text-green-400 text-xs font-mono uppercase tracking-wider">05</span>
                <span className="text-gray-600 text-xs">•</span>
                <span className="text-gray-500 text-xs uppercase tracking-wider">{t('dashboard.templates')}</span>
              </div>
              <div className="space-y-2">
                {templates.slice(0, 4).map((template, index) => (
                  <button
                    key={template.id || index}
                    onClick={() => loadTemplate(template.id)}
                    className="w-full text-left p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-all text-sm text-gray-300 hover:text-white"
                    data-testid={`template-${index}`}
                  >
                    {template.name}
                  </button>
                ))}
                {templates.length === 0 && (
                  <p className="text-gray-500 text-sm text-center py-4">Aucun template sauvegardé</p>
                )}
              </div>
            </div>

            {/* User Status */}
            <div className="glass-card p-5">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-500 text-xs uppercase tracking-wider">Compte</span>
                <span className={`w-2 h-2 rounded-full ${user?.email_verified ? 'bg-green-400' : 'bg-yellow-400'}`}></span>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Email</span>
                  <span className={`${user?.email_verified ? 'text-green-400' : 'text-yellow-400'}`}>
                    {user?.email_verified ? 'Vérifié' : 'Non vérifié'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">2FA</span>
                  <span className={`${user?.two_factor_enabled ? 'text-green-400' : 'text-gray-500'}`}>
                    {user?.two_factor_enabled ? 'Activé' : 'Désactivé'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )};

  // Pricing Component
  const Pricing = () => {
    const plans = [
      {
        id: 'starter',
        name: 'Starter',
        badge: 'PROFESSIONNEL',
        badgeColor: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
        price: '97',
        period: '/mois',
        description: 'Pour démarrer efficacement',
        features: [
          { text: '10 contenus exclusifs/mois', included: true },
          { text: 'IA spécialisée marketing', included: true },
          { text: 'Templates professionnels', included: true },
          { text: 'Support par email', included: true },
          { text: 'API access', included: false },
          { text: 'Templates sur-mesure', included: false },
        ],
        cta: 'Choisir Starter',
        ctaStyle: 'bg-blue-600 hover:bg-blue-700 text-white',
        popular: false,
      },
      {
        id: 'pro',
        name: 'Pro',
        badge: 'POPULAIRE',
        badgeColor: 'bg-green-500/20 text-green-400 border border-green-500/30',
        price: '297',
        period: '/mois',
        description: 'Pour les créateurs actifs',
        features: [
          { text: '50 contenus qualité/mois', included: true },
          { text: '8 templates sectoriels', included: true },
          { text: 'Support prioritaire français', included: true },
          { text: 'Export multi-format', included: true },
          { text: 'Analytics de base', included: true },
          { text: 'API access', included: false },
        ],
        cta: 'Choisir Pro',
        ctaStyle: 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg shadow-green-500/30',
        popular: true,
      },
      {
        id: 'business',
        name: 'Business',
        badge: 'EXCELLENCE',
        badgeColor: 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
        price: '697',
        period: '/mois',
        description: 'Pour les équipes marketing',
        features: [
          { text: '150 contenus pro/mois', included: true },
          { text: 'Templates avancés sur-mesure', included: true },
          { text: 'Optimisation conversion', included: true },
          { text: 'Analytics avancées', included: true },
          { text: 'API access complet', included: true },
          { text: 'Support dédié', included: true },
        ],
        cta: 'Choisir Business',
        ctaStyle: 'bg-purple-600 hover:bg-purple-700 text-white',
        popular: false,
      },
      {
        id: 'enterprise',
        name: 'Enterprise',
        badge: 'ÉLITE',
        badgeColor: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
        price: '1497',
        period: '/mois',
        description: 'Solutions personnalisées',
        features: [
          { text: 'Contenus illimités', included: true },
          { text: 'IA personnalisée sur-mesure', included: true },
          { text: 'White-label complet', included: true },
          { text: 'Support dédié 24/7', included: true },
          { text: 'SLA garantie 99.9%', included: true },
          { text: 'Formation équipe', included: true },
        ],
        cta: 'Contacter les ventes',
        ctaStyle: 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white',
        popular: false,
      },
    ];

    return (
      <div className="min-h-screen bg-[#0B0F19]" data-testid="pricing-page">
        {/* Header */}
        <header className="bg-[#0B0F19]/95 backdrop-blur-xl border-b border-gray-800 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex justify-between items-center">
              <button onClick={() => setCurrentPage('home')} className="flex items-center space-x-3">
                <img src="/vfa-logo.png" alt="VF Actions" className="h-10" />
              </button>
              <nav className="flex items-center space-x-4">
                <button onClick={() => setCurrentPage('home')} className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.dashboard')}
                </button>
                <button onClick={() => setCurrentPage('actualites')} className="text-gray-400 hover:text-white transition-colors">
                  Actualités
                </button>
              </nav>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="relative pt-20 pb-16 overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <div className="text-center mb-16">
              {/* Badge */}
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30 text-green-400 text-sm font-medium mb-6">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Tarification simple et transparente
              </div>

              {/* Title */}
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Choisissez votre{' '}
                <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                  accélérateur
                </span>
              </h1>
              
              <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
                Créez du contenu marketing de qualité professionnelle. 
                <span className="text-green-400"> Sans engagement, annulez à tout moment.</span>
              </p>

              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <span>Paiement sécurisé Stripe</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <span>Satisfait ou remboursé 14 jours</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <span>Support français 7j/7</span>
                </div>
              </div>
            </div>

            {/* Pricing Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {plans.map((plan) => (
                <div 
                  key={plan.id}
                  className={`relative rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02] ${
                    plan.popular 
                      ? 'bg-gradient-to-b from-green-500/20 to-transparent border-2 border-green-500/50 shadow-xl shadow-green-500/10' 
                      : 'bg-gray-900/50 border border-gray-800 hover:border-gray-700'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-4 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold rounded-full shadow-lg">
                        LE PLUS CHOISI
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-6">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-4 ${plan.badgeColor}`}>
                      {plan.badge}
                    </span>
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                    <p className="text-gray-500 text-sm mb-4">{plan.description}</p>
                    <div className="flex items-baseline justify-center">
                      {plan.price === 'Custom' ? (
                        <span className="text-3xl font-bold text-white">Sur devis</span>
                      ) : (
                        <>
                          <span className="text-5xl font-bold text-white">{plan.price}</span>
                          <span className="text-xl text-gray-400 ml-1">€{plan.period}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        {feature.included ? (
                          <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                          </svg>
                        ) : (
                          <svg className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                          </svg>
                        )}
                        <span className={feature.included ? 'text-gray-300' : 'text-gray-600'}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button 
                    onClick={() => initiatePayment(plan.id)}
                    className={`w-full py-3 rounded-xl font-semibold transition-all ${plan.ctaStyle}`}
                    data-testid={`buy-${plan.id}-btn`}
                  >
                    {plan.cta}
                  </button>
                </div>
              ))}
            </div>

            {/* FAQ Section */}
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-white text-center mb-8">Questions fréquentes</h2>
              <div className="space-y-4">
                {[
                  { q: "Puis-je changer de plan à tout moment ?", a: "Oui, vous pouvez upgrader ou downgrader votre plan à tout moment. Le changement prend effet immédiatement." },
                  { q: "Comment fonctionne le remboursement ?", a: "Nous offrons une garantie satisfait ou remboursé de 14 jours. Contactez-nous simplement et nous vous rembourserons intégralement." },
                  { q: "Les crédits non utilisés sont-ils reportés ?", a: "Les crédits sont valables pour le mois en cours. Nous vous recommandons de choisir un plan adapté à votre usage réel." },
                  { q: "Y a-t-il des frais cachés ?", a: "Non, le prix affiché est le prix final. Pas de frais d'installation, pas de frais de résiliation." },
                ].map((faq, idx) => (
                  <div key={idx} className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                    <h3 className="text-white font-medium mb-2">{faq.q}</h3>
                    <p className="text-gray-400 text-sm">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Final CTA */}
            <div className="text-center mt-16">
              <p className="text-gray-400 mb-4">Une question ? Besoin d'aide pour choisir ?</p>
              <button 
                onClick={() => window.open('mailto:contact@vfactions.eu', '_blank')}
                className="inline-flex items-center px-6 py-3 border border-gray-700 rounded-xl text-gray-300 hover:text-white hover:border-gray-600 transition-all"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
                Contactez notre équipe
              </button>
            </div>
          </div>
        </section>

        {/* Footer Mini */}
        <footer className="border-t border-gray-800 py-8">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-gray-500 text-sm">
              © 2025 VF Actions. Made with 💚 in France.
            </p>
          </div>
        </footer>
      </div>
    );
  };

  // ==================== ACTUALITES PAGE COMPONENT ====================
  const ActualitesPage = () => {
    const [activeSection, setActiveSection] = useState('all');
    const [animatedTip, setAnimatedTip] = useState(0);
    const [tipProgress, setTipProgress] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
      loadNewsData();
      loadRandomTip();
      
      // Rotate tips every 5 seconds with progress bar
      const tipInterval = setInterval(() => {
        setAnimatedTip(prev => (prev + 1) % 5);
        setTipProgress(0);
      }, 5000);

      // Progress bar animation
      const progressInterval = setInterval(() => {
        setTipProgress(prev => Math.min(prev + 2, 100));
      }, 100);
      
      return () => {
        clearInterval(tipInterval);
        clearInterval(progressInterval);
      };
    }, []);

    const getIcon = (iconName) => {
      const icons = {
        shield: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>,
        lock: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>,
        template: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"/></svg>,
        lightbulb: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>,
        clock: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>,
        target: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>,
        document: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>,
        mail: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>,
        leaf: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/></svg>,
        tree: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>,
        cpu: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"/></svg>
      };
      return icons[iconName] || icons.lightbulb;
    };

    // Only show loading spinner if there's no data at all (not even cached)
    if (!newsData) {
      return (
        <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-emerald-500 mx-auto mb-4"></div>
            <p className="text-gray-400">{t('news.loading')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-[#0B0F19]" data-testid="actualites-page">
        {/* Ambient background effects */}
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
        </div>

        {/* Loading indicator for background refresh */}
        {newsLoading && (
          <div className="fixed top-20 right-4 z-50 bg-emerald-600/90 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            <span>Mise à jour...</span>
          </div>
        )}
        
        {/* Header */}
        <header className="bg-[#0B0F19]/90 backdrop-blur-xl border-b border-gray-800 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setCurrentPage('home')}>
              <img src="/vfa-logo.png" alt="VF Actions" className="h-8" />
            </div>
            <nav className="flex items-center space-x-4">
              <button onClick={() => setCurrentPage('home')} className="text-gray-400 hover:text-emerald-400 transition-colors text-sm font-medium">Home</button>
              <button onClick={() => setCurrentPage('pricing')} className="text-gray-400 hover:text-emerald-400 transition-colors text-sm font-medium">{t('nav.pricing')}</button>
              {user && <button onClick={() => setCurrentPage('dashboard')} className="text-gray-400 hover:text-emerald-400 transition-colors text-sm font-medium">{t('nav.dashboard')}</button>}
              <LanguageSelector />
              <button 
                onClick={() => {
                  clearNewsCache();
                  loadNewsData();
                }}
                className="ml-2 bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-1.5 rounded-lg text-sm flex items-center space-x-1 transition-all border border-gray-700 hover:border-emerald-500/50"
                title="Rafraîchir les actualités"
                data-testid="refresh-news-btn"
              >
                <svg className={`w-4 h-4 ${newsLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                <span>Actualiser</span>
              </button>
            </nav>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 py-12 relative z-10">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <span className="overline mb-4 inline-block">{t('news.title')}</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('news.title')}
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Restez informé des nouveautés, astuces marketing et nos engagements RSE
            </p>
          </div>

          {/* Hero Media Section - Photos & Video */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Main Video/Image */}
            <a 
              href="https://www.youtube.com/watch?v=aircAruvnKk" 
              target="_blank" 
              rel="noopener noreferrer"
              className="md:col-span-2 relative rounded-2xl overflow-hidden group block" 
              style={{minHeight: '288px'}}
              data-testid="video-link"
            >
              <img 
                src="/news-team.jpg" 
                alt="Équipe VF Actions" 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                style={{zIndex: 1}}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" style={{zIndex: 2}}></div>
              <div className="absolute bottom-0 left-0 right-0 p-6" style={{zIndex: 3}}>
                <span className="inline-block px-3 py-1 bg-green-500 text-white text-xs font-medium rounded-full mb-3">
                  À la une
                </span>
                <h3 className="text-xl font-bold text-white mb-2">Notre équipe développe pour vous</h3>
                <p className="text-gray-300 text-sm">Innovation française au service de votre marketing</p>
              </div>
              {/* Play button */}
              <div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-all group-hover:scale-110"
                style={{zIndex: 4}}
              >
                <div className="w-20 h-20 rounded-full bg-green-500/90 backdrop-blur-sm flex items-center justify-center cursor-pointer group-hover:bg-green-500 transition-colors shadow-lg shadow-green-500/30">
                  <svg className="w-10 h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </div>
            </a>

            {/* Side Images */}
            <div className="space-y-6">
              <div className="relative rounded-2xl overflow-hidden group h-[132px]">
                <img 
                  src="/news-content.jpg" 
                  alt="Création de contenu IA" 
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  style={{zIndex: 1}}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" style={{zIndex: 2}}></div>
                <div className="absolute bottom-0 left-0 right-0 p-4" style={{zIndex: 3}}>
                  <span className="text-emerald-400 text-xs font-medium">IA</span>
                  <h4 className="text-white font-medium text-sm">Génération de contenu intelligente</h4>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden group h-[132px]">
                <img 
                  src="/news-eco.jpg" 
                  alt="Engagement écologique" 
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  style={{zIndex: 1}}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" style={{zIndex: 2}}></div>
                <div className="absolute bottom-0 left-0 right-0 p-4" style={{zIndex: 3}}>
                  <span className="text-green-400 text-xs font-medium">RSE</span>
                  <h4 className="text-white font-medium text-sm">Serveurs éco-responsables</h4>
                </div>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto mb-8 relative">
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input
              type="text"
              placeholder="Rechercher dans les actualités..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="news-search"
              data-testid="news-search-input"
            />
          </div>

          {/* Section Tabs - Modern Design */}
          <div className="flex justify-center mb-10">
            <div className="news-tabs">
              {['all', 'news', 'tips', 'rse', 'share'].map((section) => (
                <button
                  key={section}
                  onClick={() => setActiveSection(section)}
                  className={`news-tab ${activeSection === section ? 'active' : ''}`}
                  data-testid={`tab-${section}`}
                >
                  {section === 'all' && 'Toutes'}
                  {section === 'news' && t('news.newFeatures')}
                  {section === 'tips' && t('news.tips')}
                  {section === 'rse' && t('news.rse')}
                  {section === 'share' && t('news.share')}
                </button>
              ))}
            </div>
          </div>

          {/* Dynamic Tip Banner with Progress Bar */}
          {(activeSection === 'all' || activeSection === 'tips') && newsData.tips && (
            <div className="tips-carousel mb-10" data-testid="tips-carousel">
              <div className="tips-progress-bar" style={{ width: `${tipProgress}%` }}></div>
              <div className="flex items-center space-x-4">
                <div className="bg-blue-500/20 rounded-xl p-3 text-blue-400">
                  {getIcon('lightbulb')}
                </div>
                <div className="flex-1 tip-content" key={animatedTip}>
                  <span className="news-badge-tip mb-2 inline-block">{t('news.tips')}</span>
                  <p className="text-white text-lg font-medium">
                    {newsData.tips[animatedTip]?.content || currentTip}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => setAnimatedTip(prev => prev === 0 ? newsData.tips.length - 1 : prev - 1)}
                    className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-all"
                    data-testid="tip-prev-btn"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/></svg>
                  </button>
                  <button 
                    onClick={() => setAnimatedTip(prev => (prev + 1) % newsData.tips.length)}
                    className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-all"
                    data-testid="tip-next-btn"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/></svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Nouveautes Section */}
            {(activeSection === 'all' || activeSection === 'news') && (
              <div className={activeSection === 'all' ? 'lg:col-span-2' : 'lg:col-span-3'}>
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                  <span className="bg-emerald-500/20 rounded-xl p-2 mr-3 text-emerald-400">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                    </svg>
                  </span>
                  {t('news.newFeatures')}
                </h2>
                <div className="space-y-4">
                  {newsData.news?.filter(item => 
                    !searchQuery || 
                    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    item.content.toLowerCase().includes(searchQuery.toLowerCase())
                  ).map((item, index) => (
                    <div 
                      key={item.id} 
                      className={`news-card ${item.is_new ? 'news-card-new' : ''}`}
                      style={{ animationDelay: `${index * 100}ms` }}
                      data-testid={`news-item-${item.id}`}
                    >
                      <div className="flex items-start space-x-4">
                        <div className="bg-emerald-500/20 rounded-xl p-3 text-emerald-400">
                          {getIcon(item.icon)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                            {item.is_new && (
                              <span className="news-badge-new">NEW</span>
                            )}
                          </div>
                          <p className="text-gray-400">{item.content}</p>
                          <p className="text-gray-500 text-sm mt-3">
                            {new Date(item.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* RSE / Ecologie Section */}
            {(activeSection === 'all' || activeSection === 'rse') && (
              <div className={activeSection === 'rse' ? 'lg:col-span-3' : ''}>
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                  <span className="bg-emerald-500/20 rounded-xl p-2 mr-3 text-emerald-400">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064"/>
                    </svg>
                  </span>
                  {t('news.rse')}
                </h2>

                {/* Impact Stats - Modern Widget */}
                <div className="eco-impact-widget mb-6" data-testid="eco-impact-widget">
                  <span className="overline mb-4 inline-block">Notre Impact Global</span>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div className="eco-stat">
                      <p className="eco-stat-value">{newsData.rse?.impact_global?.co2_economise_kg?.toFixed(1)}</p>
                      <p className="eco-stat-label">kg CO₂ économisés</p>
                    </div>
                    <div className="eco-stat">
                      <p className="eco-stat-value">{newsData.rse?.impact_global?.arbres_plantes}</p>
                      <p className="eco-stat-label">arbres plantés</p>
                    </div>
                    <div className="eco-stat">
                      <p className="eco-stat-value">{newsData.rse?.impact_global?.utilisateurs_engages}</p>
                      <p className="eco-stat-label">utilisateurs engagés</p>
                    </div>
                    <div className="eco-stat">
                      <p className="eco-stat-value">{newsData.rse?.impact_global?.actions_vertes}</p>
                      <p className="eco-stat-label">actions vertes</p>
                    </div>
                  </div>
                </div>

                {/* Engagements */}
                <div className="space-y-3 mb-6">
                  {newsData.rse?.engagements?.map((engagement, index) => (
                    <div key={engagement.id} className="news-card flex items-center space-x-3" data-testid={`engagement-${engagement.id}`}>
                      <div className="bg-emerald-500/20 rounded-xl p-2 text-emerald-400">
                        {getIcon(engagement.icon)}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">{engagement.title}</p>
                        <p className="text-gray-400 text-sm">{engagement.description}</p>
                      </div>
                      <span className="news-badge-eco">{engagement.status}</span>
                    </div>
                  ))}
                </div>

                {/* Objectifs 2025 */}
                <div className="news-card" data-testid="objectives-2025">
                  <h4 className="text-white font-semibold mb-4">Objectifs 2025</h4>
                  <div className="space-y-4">
                    {newsData.rse?.objectifs_2025?.map((obj, index) => (
                      <div key={index}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-300">{obj.label}</span>
                          <span className="text-emerald-400 font-mono">{obj.progress}%</span>
                        </div>
                        <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 rounded-full transition-all duration-1000"
                            style={{ width: `${obj.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Conseil Eco */}
                {newsData.rse?.conseil_eco && (
                  <div className="mt-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4" data-testid="eco-tip">
                    <p className="text-emerald-300 text-sm flex items-start">
                      <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                      </svg>
                      {newsData.rse.conseil_eco}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Astuces Section */}
            {activeSection === 'tips' && (
              <div className="lg:col-span-3">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                  <span className="bg-amber-500/30 rounded-lg p-2 mr-3">
                    {getIcon('lightbulb')}
                  </span>
                  Astuces Marketing
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {newsData.tips?.map((tip, index) => (
                    <div 
                      key={tip.id}
                      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-all duration-300 hover:scale-105"
                    >
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="bg-amber-500/30 rounded-lg p-2 text-amber-400">
                          {getIcon(tip.icon)}
                        </div>
                        <span className="bg-amber-500/20 text-amber-300 text-xs px-2 py-1 rounded-full">{tip.category}</span>
                      </div>
                      <h3 className="text-white font-semibold mb-2">{tip.title}</h3>
                      <p className="text-gray-400 text-sm">{tip.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Share / QR Code Section */}
            {activeSection === 'share' && (
              <div className="lg:col-span-3">
                <div className="max-w-2xl mx-auto">
                  <h2 className="text-2xl font-bold text-white mb-6 text-center flex items-center justify-center">
                    <span className="bg-blue-500/30 rounded-lg p-2 mr-3">
                      <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
                      </svg>
                    </span>
                    Partagez VF ACTIONS
                  </h2>

                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 text-center">
                    <p className="text-gray-300 mb-6">
                      Scannez ce QR code pour acceder instantanement a VF ACTIONS depuis votre smartphone
                    </p>
                    
                    {newsData.site_qr_code && (
                      <div className="bg-white rounded-2xl p-6 inline-block mb-6 shadow-2xl shadow-purple-500/20">
                        <img 
                          src={newsData.site_qr_code} 
                          alt="QR Code VF ACTIONS" 
                          className="w-64 h-64 mx-auto"
                          data-testid="site-qr-code"
                        />
                      </div>
                    )}

                    <p className="text-gray-400 text-sm mb-4">{newsData.site_url}</p>

                    <div className="flex justify-center space-x-4">
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(newsData.site_url);
                          alert('Lien copie dans le presse-papier !');
                        }}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all"
                      >
                        Copier le lien
                      </button>
                      <button 
                        onClick={() => {
                          if (navigator.share) {
                            navigator.share({
                              title: 'VF ACTIONS',
                              text: 'Decouvrez VF ACTIONS - Generation de contenu IA',
                              url: newsData.site_url
                            });
                          } else {
                            alert('Partage non supporte sur ce navigateur');
                          }
                        }}
                        className="bg-white/10 border border-white/20 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/20 transition-all"
                      >
                        Partager
                      </button>
                    </div>
                  </div>

                  {/* Social Share Options */}
                  <div className="mt-8 text-center">
                    <p className="text-gray-400 mb-4">Ou partagez sur vos reseaux</p>
                    <div className="flex justify-center space-x-4">
                      <a 
                        href={`https://twitter.com/intent/tweet?text=Decouvrez%20VF%20ACTIONS%20-%20Generation%20de%20contenu%20IA&url=${encodeURIComponent(newsData.site_url)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-blue-500/20 border border-blue-500/30 text-blue-400 px-4 py-2 rounded-lg hover:bg-blue-500/30 transition-all"
                      >
                        Twitter
                      </a>
                      <a 
                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(newsData.site_url)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-blue-700/20 border border-blue-700/30 text-blue-300 px-4 py-2 rounded-lg hover:bg-blue-700/30 transition-all"
                      >
                        LinkedIn
                      </a>
                      <a 
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(newsData.site_url)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-blue-600/20 border border-blue-600/30 text-blue-400 px-4 py-2 rounded-lg hover:bg-blue-600/30 transition-all"
                      >
                        Facebook
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-black/30 border-t border-white/10 mt-16 py-8">
          <div className="max-w-7xl mx-auto px-4 text-center text-gray-400">
            <p>VF ACTIONS - Generation de contenu IA responsable</p>
            <p className="text-sm mt-2">Derniere mise a jour: {new Date(newsData.last_updated).toLocaleString('fr-FR')}</p>
          </div>
        </footer>
      </div>
    );
  };

  // Admin Dashboard Component
  const AdminDashboard = () => {
    // Load users when switching to users view
    useEffect(() => {
      if (adminView === 'users' && adminUsers.length === 0) {
        loadAdminUsers();
      }
    }, [adminView]);
    
    return (
      <div className="min-h-screen bg-gray-900" data-testid="admin-dashboard">
        <header className="bg-red-600 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-red-800 rounded-2xl flex items-center justify-center">
                  <span className="text-white font-bold text-xl">VF</span>
                </div>
                <h1 className="text-2xl font-bold text-white">VF ACTIONS - Admin</h1>
              </div>
              <nav className="flex space-x-4">
                <button 
                  onClick={() => setCurrentPage('home')}
                  className="text-red-100 hover:text-white font-medium"
                >
                  Home
                </button>
                <button 
                  onClick={() => setCurrentPage('dashboard')}
                  className="text-red-100 hover:text-white font-medium"
                >
                  Dashboard
                </button>
              </nav>
            </div>
          </div>
        </header>

        {/* Admin Navigation Tabs */}
        <div className="bg-gray-800 border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-4">
              <button
                onClick={() => setAdminView('stats')}
                className={`py-4 px-6 font-medium ${adminView === 'stats' 
                  ? 'text-white border-b-2 border-red-500' 
                  : 'text-gray-400 hover:text-white'}`}
              >
                Statistiques
              </button>
              <button
                onClick={() => {
                  setAdminView('analytics');
                  if (!advancedAnalytics) loadAdvancedAnalytics();
                }}
                className={`py-4 px-6 font-medium ${adminView === 'analytics' 
                  ? 'text-white border-b-2 border-red-500' 
                  : 'text-gray-400 hover:text-white'}`}
              >
                Analytics & Previsions
              </button>
              <button
                onClick={() => setAdminView('users')}
                className={`py-4 px-6 font-medium ${adminView === 'users' 
                  ? 'text-white border-b-2 border-red-500' 
                  : 'text-gray-400 hover:text-white'}`}
              >
                Utilisateurs
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          
          {/* Stats View */}
          {adminView === 'stats' && (
            <>
              <div className="grid md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Utilisateurs</p>
                      <p className="text-2xl font-bold text-gray-900">{adminStats?.totalUsers || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Abonnements</p>
                      <p className="text-2xl font-bold text-gray-900">{adminStats?.activeSubscriptions || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Revenus/mois</p>
                      <p className="text-2xl font-bold text-gray-900">EUR{adminStats?.monthlyRevenue?.toLocaleString() || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Contenus</p>
                      <p className="text-2xl font-bold text-gray-900">{adminStats?.contentGenerated?.toLocaleString() || 0}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-4">Repartition des Revenus</h3>
                  <div className="space-y-3">
                    {[
                      {plan: 'Enterprise', price: '1,497EUR', color: 'bg-yellow-500', pct: 48},
                      {plan: 'Business', price: '697EUR', color: 'bg-purple-500', pct: 34},
                      {plan: 'Pro', price: '297EUR', color: 'bg-green-500', pct: 14},
                      {plan: 'Starter', price: '97EUR', color: 'bg-blue-500', pct: 4}
                    ].map(item => (
                      <div key={item.plan}>
                        <div className="flex justify-between text-sm mb-1">
                          <span>{item.plan} ({item.price}/mois)</span>
                          <span className="font-bold">{item.pct}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className={`${item.color} h-2 rounded-full`} style={{width: `${item.pct}%`}}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-4">Metriques Cles</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">Taux de Conversion</span>
                      <span className="font-bold text-green-600 text-xl">{adminStats?.conversionRate || 0}%</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">Plan Populaire</span>
                      <span className="font-bold text-purple-600 text-xl">{adminStats?.topPlan || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">Revenus Annuels</span>
                      <span className="font-bold text-blue-600 text-xl">EUR{((adminStats?.monthlyRevenue || 0) * 12).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Analytics View */}
          {adminView === 'analytics' && (
            <div className="space-y-6">
              {!advancedAnalytics ? (
                <div className="text-center py-12">
                  <p className="text-gray-400">Chargement des analytics...</p>
                </div>
              ) : (
                <>
                  {/* KPI Cards Row 1 - Revenue */}
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white">
                      <p className="text-green-100 text-sm font-medium">MRR (Mensuel)</p>
                      <p className="text-3xl font-bold mt-1">EUR{advancedAnalytics.mrr?.toLocaleString()}</p>
                      <p className="text-green-200 text-xs mt-2">Revenus recurrents mensuels</p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
                      <p className="text-blue-100 text-sm font-medium">ARR (Annuel)</p>
                      <p className="text-3xl font-bold mt-1">EUR{advancedAnalytics.arr?.toLocaleString()}</p>
                      <p className="text-blue-200 text-xs mt-2">Projection annuelle</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
                      <p className="text-purple-100 text-sm font-medium">ARPU</p>
                      <p className="text-3xl font-bold mt-1">EUR{advancedAnalytics.ratios?.arpu}</p>
                      <p className="text-purple-200 text-xs mt-2">Revenu moyen par utilisateur</p>
                    </div>
                    <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl p-5 text-white">
                      <p className="text-pink-100 text-sm font-medium">LTV</p>
                      <p className="text-3xl font-bold mt-1">EUR{advancedAnalytics.ratios?.ltv?.toLocaleString()}</p>
                      <p className="text-pink-200 text-xs mt-2">Valeur vie client (12 mois)</p>
                    </div>
                  </div>

                  {/* KPI Cards Row 2 - Users */}
                  <div className="grid md:grid-cols-5 gap-4">
                    <div className="bg-white rounded-xl p-5 shadow">
                      <p className="text-gray-500 text-sm">Total Utilisateurs</p>
                      <p className="text-2xl font-bold text-gray-900">{advancedAnalytics.total_users}</p>
                      <p className="text-green-500 text-xs mt-1">+{advancedAnalytics.new_users_30d} ce mois</p>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow">
                      <p className="text-gray-500 text-sm">Utilisateurs Payants</p>
                      <p className="text-2xl font-bold text-gray-900">{advancedAnalytics.paid_users}</p>
                      <p className="text-blue-500 text-xs mt-1">{advancedAnalytics.ratios?.conversion_rate}% conversion</p>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow">
                      <p className="text-gray-500 text-sm">Utilisateurs Actifs</p>
                      <p className="text-2xl font-bold text-gray-900">{advancedAnalytics.monthly_active_users}</p>
                      <p className="text-purple-500 text-xs mt-1">{advancedAnalytics.ratios?.mau_rate}% MAU rate</p>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow">
                      <p className="text-gray-500 text-sm">Utilisateurs Engages</p>
                      <p className="text-2xl font-bold text-gray-900">{advancedAnalytics.engaged_users}</p>
                      <p className="text-orange-500 text-xs mt-1">{advancedAnalytics.ratios?.engagement_rate}% engagement</p>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow">
                      <p className="text-gray-500 text-sm">Contenus/User</p>
                      <p className="text-2xl font-bold text-gray-900">{advancedAnalytics.ratios?.content_per_user}</p>
                      <p className="text-gray-400 text-xs mt-1">moyenne</p>
                    </div>
                  </div>

                  {/* Ratios Section */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl p-6 shadow">
                      <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"></path></svg>
                        </span>
                        Ratios Cles
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div>
                            <span className="text-gray-700 font-medium">Taux de Conversion</span>
                            <p className="text-xs text-gray-500">Découverte → Premium</p>
                          </div>
                          <span className="text-2xl font-bold text-green-600">{advancedAnalytics.ratios?.conversion_rate}%</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div>
                            <span className="text-gray-700 font-medium">Taux de Retention</span>
                            <p className="text-xs text-gray-500">Clients conserves</p>
                          </div>
                          <span className="text-2xl font-bold text-blue-600">{advancedAnalytics.ratios?.retention_rate}%</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div>
                            <span className="text-gray-700 font-medium">Taux de Churn</span>
                            <p className="text-xs text-gray-500">Clients perdus</p>
                          </div>
                          <span className="text-2xl font-bold text-red-500">{advancedAnalytics.ratios?.churn_rate}%</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div>
                            <span className="text-gray-700 font-medium">Croissance Mensuelle</span>
                            <p className="text-xs text-gray-500">Nouveaux utilisateurs</p>
                          </div>
                          <span className="text-2xl font-bold text-purple-600">+{advancedAnalytics.ratios?.monthly_growth_rate}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Forecasting */}
                    <div className="bg-white rounded-xl p-6 shadow">
                      <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>
                        </span>
                        Previsions de Revenus
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg">
                          <span className="text-gray-600">Actuel</span>
                          <span className="font-bold text-gray-900">EUR{advancedAnalytics.projections?.revenue?.current?.toLocaleString()}/mois</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-gradient-to-r from-green-50 to-green-100 rounded-lg">
                          <span className="text-gray-600">Dans 3 mois</span>
                          <span className="font-bold text-green-700">EUR{Math.round(advancedAnalytics.projections?.revenue?.month_3)?.toLocaleString()}/mois</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg">
                          <span className="text-gray-600">Dans 6 mois</span>
                          <span className="font-bold text-blue-700">EUR{Math.round(advancedAnalytics.projections?.revenue?.month_6)?.toLocaleString()}/mois</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg">
                          <span className="text-gray-600">Dans 12 mois</span>
                          <span className="font-bold text-purple-700">EUR{Math.round(advancedAnalytics.projections?.revenue?.month_12)?.toLocaleString()}/mois</span>
                        </div>
                        <div className="mt-4 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
                          <p className="text-sm text-gray-600">Total prevu sur 12 mois</p>
                          <p className="text-3xl font-bold text-orange-600">EUR{Math.round(advancedAnalytics.projections?.revenue?.year_1_total)?.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* User Growth Projections */}
                  <div className="bg-white rounded-xl p-6 shadow">
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                      <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                      </span>
                      Previsions d'Utilisateurs
                    </h3>
                    <div className="grid grid-cols-4 gap-4">
                      {[
                        {label: 'Actuel', value: advancedAnalytics.projections?.users?.month_1, color: 'gray'},
                        {label: '3 mois', value: advancedAnalytics.projections?.users?.month_3, color: 'green'},
                        {label: '6 mois', value: advancedAnalytics.projections?.users?.month_6, color: 'blue'},
                        {label: '12 mois', value: advancedAnalytics.projections?.users?.month_12, color: 'purple'}
                      ].map(item => (
                        <div key={item.label} className={`text-center p-4 rounded-lg bg-${item.color}-50`}>
                          <p className="text-sm text-gray-500">{item.label}</p>
                          <p className={`text-2xl font-bold text-${item.color}-600`}>{item.value?.toLocaleString()}</p>
                          <p className="text-xs text-gray-400">utilisateurs</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Plan Distribution & Content Stats */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl p-6 shadow">
                      <h3 className="text-lg font-semibold mb-4">Distribution des Plans</h3>
                      <div className="space-y-3">
                        {[
                          {plan: 'Starter', count: advancedAnalytics.plan_distribution?.starter, color: 'blue', price: 97},
                          {plan: 'Pro', count: advancedAnalytics.plan_distribution?.pro, color: 'green', price: 297},
                          {plan: 'Business', count: advancedAnalytics.plan_distribution?.business, color: 'purple', price: 697},
                          {plan: 'Enterprise', count: advancedAnalytics.plan_distribution?.enterprise, color: 'yellow', price: 1497}
                        ].map(item => {
                          const total = advancedAnalytics.total_users || 1;
                          const pct = Math.round((item.count / total) * 100);
                          return (
                            <div key={item.plan}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="font-medium">{item.plan} ({item.price}EUR)</span>
                                <span>{item.count} ({pct}%)</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3">
                                <div className={`bg-${item.color}-500 h-3 rounded-full transition-all duration-500`} style={{width: `${pct}%`}}></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-6 shadow">
                      <h3 className="text-lg font-semibold mb-4">Contenus par Type</h3>
                      <div className="space-y-3">
                        {Object.entries(advancedAnalytics.content_by_type || {}).map(([type, count]) => {
                          const total = advancedAnalytics.total_content || 1;
                          const pct = Math.round((count / total) * 100);
                          const colors = {
                            blog: 'blue', social: 'pink', newsletter: 'green', 
                            product: 'orange', sales: 'purple'
                          };
                          return (
                            <div key={type}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="font-medium capitalize">{type}</span>
                                <span>{count} ({pct}%)</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3">
                                <div className={`bg-${colors[type] || 'gray'}-500 h-3 rounded-full`} style={{width: `${pct}%`}}></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Loyalty & Engagement */}
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
                    <h3 className="text-lg font-semibold mb-4">Programme de Fidelite</h3>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="bg-white/20 rounded-lg p-4 text-center">
                        <p className="text-indigo-100 text-sm">Points Totaux</p>
                        <p className="text-3xl font-bold">{advancedAnalytics.loyalty?.total_points?.toLocaleString()}</p>
                      </div>
                      <div className="bg-white/20 rounded-lg p-4 text-center">
                        <p className="text-indigo-100 text-sm">Moyenne/User</p>
                        <p className="text-3xl font-bold">{Math.round(advancedAnalytics.loyalty?.avg_points_per_user)}</p>
                      </div>
                      <div className="bg-white/20 rounded-lg p-4 text-center">
                        <p className="text-indigo-100 text-sm">Total Contenus</p>
                        <p className="text-3xl font-bold">{advancedAnalytics.total_content?.toLocaleString()}</p>
                      </div>
                      <div className="bg-white/20 rounded-lg p-4 text-center">
                        <p className="text-indigo-100 text-sm">Templates Crees</p>
                        <p className="text-3xl font-bold">{advancedAnalytics.total_templates}</p>
                      </div>
                    </div>
                  </div>

                  {/* Refresh Button */}
                  <div className="text-center">
                    <button
                      onClick={loadAdvancedAnalytics}
                      className="bg-gray-700 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      Actualiser les Analytics
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Users View */}
          {adminView === 'users' && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-semibold">Gestion des Utilisateurs ({adminUsers.length})</h3>
                <button
                  onClick={loadAdminUsers}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200"
                >
                  Actualiser
                </button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisateur</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Credits</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Points</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {adminUsers.map(u => (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900">{u.name}</p>
                            <p className="text-sm text-gray-500">{u.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            u.subscription_plan === 'enterprise' ? 'bg-yellow-100 text-yellow-800' :
                            u.subscription_plan === 'business' ? 'bg-purple-100 text-purple-800' :
                            u.subscription_plan === 'pro' ? 'bg-green-100 text-green-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {u.subscription_plan}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {u.credits_used}/{u.credits_limit === -1 ? '∞' : u.credits_limit}
                        </td>
                        <td className="px-4 py-3 text-sm">{u.loyalty_points}</td>
                        <td className="px-4 py-3">
                          <div className="flex space-x-1">
                            {u.is_admin && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">Admin</span>
                            )}
                            {!u.is_active && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">Inactif</span>
                            )}
                            {u.is_active && !u.is_admin && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Actif</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => viewUserDetails(u.id)}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Details
                            </button>
                            <select
                              value={u.subscription_plan}
                              onChange={(e) => updateUserPlan(u.id, e.target.value)}
                              className="text-sm border rounded px-2 py-1"
                            >
                              <option value="starter">Starter</option>
                              <option value="pro">Pro</option>
                              <option value="business">Business</option>
                              <option value="enterprise">Enterprise</option>
                            </select>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* User Detail Modal */}
        {showUserModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Details Utilisateur</h2>
                <button onClick={() => setShowUserModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Nom</p>
                  <p className="font-semibold">{selectedUser.user?.name}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-semibold">{selectedUser.user?.email}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Plan</p>
                  <p className="font-semibold capitalize">{selectedUser.user?.subscription_plan}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Credits</p>
                  <p className="font-semibold">
                    {selectedUser.user?.credits_used} / {selectedUser.user?.credits_limit === -1 ? '∞' : selectedUser.user?.credits_limit}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Points Fidelite</p>
                  <p className="font-semibold">{selectedUser.user?.loyalty_points} ({selectedUser.user?.loyalty_level})</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Contenus Generes</p>
                  <p className="font-semibold">{selectedUser.stats?.content_count || 0}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Actions Rapides</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <button
                    onClick={() => adjustUserCredits(selectedUser.user?.id, 10, 'Bonus admin')}
                    className="bg-green-100 text-green-700 px-3 py-2 rounded-lg text-sm hover:bg-green-200"
                  >
                    +10 Credits
                  </button>
                  <button
                    onClick={() => adjustUserCredits(selectedUser.user?.id, -5, 'Ajustement admin')}
                    className="bg-red-100 text-red-700 px-3 py-2 rounded-lg text-sm hover:bg-red-200"
                  >
                    -5 Credits
                  </button>
                  <button
                    onClick={() => generateResetLinkForUser(selectedUser.user?.id)}
                    className="bg-blue-100 text-blue-700 px-3 py-2 rounded-lg text-sm hover:bg-blue-200"
                  >
                    Reset MDP
                  </button>
                  <button
                    onClick={() => toggleUserAdmin(selectedUser.user?.id, !selectedUser.user?.is_admin)}
                    className={`px-3 py-2 rounded-lg text-sm ${
                      selectedUser.user?.is_admin 
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                        : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                    }`}
                  >
                    {selectedUser.user?.is_admin ? 'Retirer Admin' : 'Rendre Admin'}
                  </button>
                </div>
              </div>

              {selectedUser.user?.is_active && (
                <div className="border-t pt-4 mt-4">
                  <button
                    onClick={() => deactivateUser(selectedUser.user?.id)}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                  >
                    Desactiver ce compte
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCurrentPage = () => {
    switch(currentPage) {
      case 'home':
        return <HomePage />;
      case 'dashboard':
        return user ? <Dashboard /> : <HomePage />;
      case 'pro':
        return user ? (
          <ProDashboard 
            user={user} 
            onLogout={handleLogout} 
            onBack={() => setCurrentPage('home')}
          />
        ) : <HomePage />;
      case 'wizard':
        return (
          <StrategyWizard 
            onComplete={(result) => {
              setStrategyPreview(result);
              setCurrentPage('preview');
            }}
            onBack={() => setCurrentPage('landing')}
            isDemo={!user}
            initialBusinessType={wizardInitialType}
          />
        );
      case 'preview':
        return (
          <StrategyPreview 
            preview={strategyPreview}
            onCreateAccount={() => {
              // Store project ID for conversion after registration
              if (strategyPreview?.project_id) {
                setPendingProjectId(strategyPreview.project_id);
              }
              setShowAuthModal(true);
              setAuthMode('register');
            }}
            onBack={() => setCurrentPage('wizard')}
            onViewFull={() => {
              if (strategyPreview?.project_id) {
                setCurrentProjectId(strategyPreview.project_id);
                setCurrentPage('strategy');
              }
            }}
          />
        );
      case 'strategy':
        return (
          <FullStrategyView 
            projectId={currentProjectId}
            user={user}
            onBack={() => {
              setCurrentProjectId(null);
              setCurrentPage('projects');
            }}
          />
        );
      case 'projects':
        return (
          <ProjectsDashboard 
            onSelectProject={(projectId) => {
              setCurrentProjectId(projectId);
              setCurrentPage('strategy');
            }}
            onNewProject={() => setCurrentPage('wizard')}
            onBack={() => setCurrentPage('home')}
          />
        );
      case 'landing':
        return (
          <NewLandingPage 
            onStartDemo={(businessType) => {
              setWizardInitialType(businessType || null);
              setCurrentPage('wizard');
            }}
            onLogin={() => {
              setShowAuthModal(true);
              setAuthMode('login');
            }}
            onRegister={() => {
              setShowAuthModal(true);
              setAuthMode('register');
            }}
          />
        );
      case 'pricing':
        return <Pricing />;
      case 'actualites':
        return <ActualitesPage />;
      case 'admin':
        return (isAdmin && user?.is_admin) ? <AdminDashboard /> : <HomePage />;
      case 'notre-approche':
        return <NotreApproche onNavigate={setCurrentPage} />;
      case 'mentions-legales':
        return <MentionsLegales />;
      case 'cgu':
        return <CGU />;
      case 'confidentialite':
        return <Confidentialite />;
      case 'rgpd':
        return <RGPD />;
      default:
        return <HomePage />;
    }
  };

  return (
    <div className="App">
      {showAuthModal && <AuthModal />}
      {showSaveTemplateModal && <SaveTemplateModal />}
      {showResetPasswordModal && <ResetPasswordModal />}
      {show2FASetup && <TwoFASetupModal />}
      {showBackupCodes && <BackupCodesModal />}
      {renderCurrentPage()}
    </div>
  );
}

export default App;
